/* ttg-family.js — Familien-Konfigurator (Fußmatte Strichmännchen).
 *
 * Eigenständige schlanke Schicht NEBEN der Engine (kein Engine-Step, null
 * Engine-Diff — Architektur-Freigabe 20.08.2026). Layout-Mathematik ist die
 * verbindliche 1:1-Portierung von computeLayout()/renderDesign() aus
 * docs/fussmatten-konfigurator-prototyp-v6.html; Äquivalenz hält
 * test/family-layout.mjs (Gegenrechnung gegen den extrahierten Prototyp-Code).
 *
 * Aufbau:
 *  - Pure Layout-/Render-/Property-Funktionen (Node-fähig, Messfunktion wird
 *    injiziert) — getestet in test/family-layout.mjs und test/family-cart.mjs
 *  - Custom Element <ttg-family-customizer> (nur im Browser registriert):
 *    Canvas-Vorschau mit Rahmen-Overlay, Mitglieder-UI, Varianten-Sync und
 *    Property-Sync ans native Produktformular (Cart-Handover Variante B,
 *    gleiche Mechanik wie ttg-theme-adapter.js: hidden inputs im Form,
 *    Leerwerte werden verworfen).
 *
 * Der Figurenkatalog kommt aus assets/ttg-familienfiguren.json (version 3).
 * Relative Figurhöhe wird IMMER als h/dadHeightPx gerechnet, nie hart codiert;
 * Herz-Seitenverhältnis und Rahmen-Geometrie ebenso aus dem Katalog.
 */

// ---------- Layout-Konstanten (Design-Basis 600 × 400 mm, Querformat) ----------
// 50x75 ist exakt dasselbe Layout mit Faktor 1,25 — es gibt nur EINE
// Layout-Berechnung auf 600×400-Basis (Briefing-Regel 7).
export const FAMILY_LAYOUT = {
  W: 600, H: 400,
  margin: 30,
  headSizeMax: 30,     // eine Größe für BEIDE Kopfzeilen
  headSizeMin: 16,
  heartH: 80,          // Herzhöhe, überspannt beide Zeilen
  heartTop: 22,
  heartGap: 8,
  line1Y: 64,          // Grundlinie Zeile 1
  nameY: 112,          // Grundlinie Familienname
  baseFigureH: 132,
  baselineY: 336,      // gemeinsame Standlinie der Figuren
  memberNameSizeMax: 24,
  // Namens-Minimum: Vorgabe Jens 31.08.2026 (vorher 13 aus dem Prototyp).
  // Der Boden der Schrumpfschleife war die letzte Ursache dafuer, dass lange
  // Namen bei linksbuendiger Reihe in die 20-mm-Sicherheitszone liefen — die
  // Schleife hoerte 1 mm zu frueh auf. Gemessen (Pillow im Druckmassstab,
  // echte Schrift): „Maximilian" linker Rand 17,67 -> 20,13 mm, „Konstanze"
  // 18,60 -> 20,97 mm. Betroffen sind AUSSCHLIESSLICH Namen, die den Boden
  // ueberhaupt erreichen; alles darueber bleibt unveraendert.
  // Die Fuesse-Strecke behaelt BEWUSST 13 — sie ist dort schon sauber
  // (s. FUESSE_LAYOUT und test/fuesse-layout.mjs).
  memberNameSizeMin: 12,
  memberNameY: 366,    // Grundlinie Mitgliedsnamen
  line1Text: '- LICH WILLKOMMEN BEI FAMILIE',
  // Sicherheitszone (Layout-Pruefung 31.08.2026): Abstand zur Mattenkante, in
  // dem keine Tinte liegen darf — Gummirand und Anschnitt fressen ihn auf.
  // 20 mm auf allen vier Seiten, gleicher Wert wie die Klemmung der
  // beweglichen Gruppe in ttg-engine.js (_safeBoundsPct).
  safeInsetMm: 20,
};

// Stash-Schluessel des Vorschaubild-Uploaders (assets/ttg-preview-uploader.js).
// MUSS woertlich derselbe sein — der Uploader liest genau diesen Schluessel;
// ein Tippfehler hier fuehrt zu einem Auftrag, den niemand abholt, und das
// voellig lautlos.
export const FAMILY_PREVIEW_STASH = 'ttg:pendingPreview';

// Obergrenze fuer den Fallback OHNE Stash (privater Modus, voller Speicher).
// 8 s deckt einen Railway-Cold-Start mit einem Wiederholungsversuch ab; der
// Uploader selbst arbeitet mit 20 s Zeitgrenze je Upload und drei Versuchen,
// darauf zu warten waere fuer einen wartenden Kunden zu lang. Laeuft die Frist
// ab, wird trotzdem weitergeleitet und der Fehlschlag protokolliert.
export const FAMILY_PREVIEW_TIMEOUT_MS = 8000;

// Runde 3 (08.10.2026, M1): so lange haelt ein Kaufklick an, wenn das Exportbild der Mattenfarbe (1600) noch fehlt.
// Danach sichtbare Meldung und kein Add-to-Cart — das Vorschaubild entsteht nie aus dem Anzeigebild.
export const FAMILY_EXPORT_WARTEN_MS = 10000;

export const FAMILY_LIMITS = {
  maxMembers: 8,
  // 12 statt 10 (Freigabe Jens 26.10.2026, Anlass #1006152: „Maily und Jo"
  // wurde still auf „Maily und" gekuerzt). An der Grenze erscheint ein Hinweis
  // unter der Zeile (familyNameInput / FAMILY_NAME_LIMIT_HINT).
  nameMax: 12,
  famNameMax: 20,
  // Abstandsgrenzen: Vorgabe Jens 21.08.2026 (vorher 6/30/4/14 aus dem
  // Prototyp). Schrittweite 5 statt 4, damit jede erreichbare Stufe eine
  // runde Zahl ist: 10, 15, 20 … 50. Beide Weiten treffen das Maximum
  // genau; 5 braucht dafuer 8 Klicks statt 10.
  //
  // Zur Wirkung des Maximums, gemessen mit der echten Layoutfunktion
  // (scripts/mess-abstand-grenzen.mjs): Bis SECHS Figuren wirkt der
  // eingestellte Abstand exakt. Erst ab acht Figuren greift die
  // Gruppenskalierung (ab 23 mm) und staucht Figuren UND Luecken, damit der
  // Rand gewahrt bleibt — bei 8 Figuren und 50 mm bleiben 97 mm Figurenhoehe
  // (von 132) und eine tatsaechliche Luecke von 37 mm. Der Regler ist dort
  // also ein Wunsch, kein Massband. Das ist Bestandsverhalten und gilt heute
  // schon bei 8 Figuren ab 23 mm.
  gapMin: 10,
  gapMax: 50,
  gapStep: 5,
  gapDefault: 30,
};

// CDN-Rendition: width-Parameter an eine Shopify-CDN-URL hängen. Die
// Vorschau braucht nie die Rohdatei (schwarzer Matten-Hintergrund roh:
// 4752×3168, 1,9 MB — Perf-Messung 20.08.2026); der Druck-Renderer
// (ttg-mattenrender) lädt serverseitig weiterhin die Roh-URLs.
export function cdnSize(url, width) {
  if (!url) return url;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
}

// Anzeige-Rendition des Mattenhintergrunds (offene Punkte 08.10.2026, Teil C): Breite nach Darstellung statt fest 1600 —
// Bühnenbreite × devicePixelRatio × Fensteranteil des Rahmens, aufgerundet auf 200er-Stufen (CDN-Cache), 400–1600. Gemessen 08.10. (schwarz):
// 1600 = 322 KB (WebP), 800 = 61 KB, 600 = 30 KB. Das Vorschaubild (_preview_image) nutzt weiter die 1600er-Fassung,
// damit es sich nicht ändert (eigenes Exportbild je Mattenfarbe, lädt nach dem Aufbau mit niedriger Priorität).
export function anzeigeBreite(cssBreite, dpr, anteil = 1) {
  // anteil = Breite des Rahmenfensters / Bühnenbreite (der Hintergrund füllt nur das Fenster); 200er-Stufen, 400–1600.
  const px = (cssBreite > 0 ? cssBreite : 800) * (dpr > 0 ? dpr : 1) * (anteil > 0 && anteil <= 1 ? anteil : 1);
  return Math.min(1600, Math.max(400, Math.ceil(px / 200) * 200));
}

// Cent-Preis → deutsche Anzeige („28,90 €").
export function formatMoneyEUR(cents) {
  if (cents == null || isNaN(cents)) return '';
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}

export const FAMILY_FONT = 'Cabin Sketch';

export const HEART_LABELS = { rot: 'Rot', weiss: 'Weiß' };
export const ALIGN_LABELS = { left: 'Links', center: 'Mittig', right: 'Rechts' };

// Druckmaße je Variante (Briefing: 300 dpi)
export const FAMILY_PRINT_SIZES = {
  '40x60': { w: 7087, h: 4724 },
  '50x75': { w: 8858, h: 5906 },
};

// Optionsnamen des Basisprodukts (Briefing Session 2); per Config überschreibbar.
export const FAMILY_OPTION_NAMES = { mat: 'Mattenfarbe', size: 'Größe' };

// ---------- Katalog-Helfer ----------

// {key: {label,url,w,h,rel}} — rel = h / dadHeightPx (Vater = 1.0)
export function buildFigureMap(catalog) {
  const map = {};
  for (const f of catalog.figuren) {
    map[f.key] = { ...f, rel: f.h / catalog.dadHeightPx };
  }
  return map;
}

// Beide Herzvarianten teilen dasselbe Seitenverhältnis (895/947).
export function heartAspect(catalog) {
  return catalog.heart.rot.w / catalog.heart.rot.h;
}

// Messfunktion aus einem echten 2D-Kontext bauen (Browser-Pfad).
// Die Node-Tests injizieren stattdessen eine deterministische Stub-Metrik.
export function makeCtxMeasure(ctx, family = FAMILY_FONT) {
  return (text, size) => {
    ctx.font = `${size}px '${family}'`;
    return ctx.measureText(text).width;
  };
}

// ---------- Layout berechnen (mm, Basis 600×400) ----------
// 1:1-Portierung von computeLayout(ctx) aus dem Prototyp. env:
//   figures  — buildFigureMap(catalog)
//   heartAR  — heartAspect(catalog)
//   measure  — (text, sizeMm) => Breite in mm
//   L        — optional, Default FAMILY_LAYOUT (nur für Tests/Sonden)
export function computeFamilyLayout(state, env) {
  const { figures, heartAR, measure } = env;
  const L = env.L || FAMILY_LAYOUT;
  const usableW = L.W - 2 * L.margin;
  const out = { header: {}, figures: [], nameFont: 0 };

  // Beide Kopfzeilen in EINER Größe; schrumpfen gemeinsam, wenn nötig
  const t1 = L.line1Text;
  const nameTxt = (state.famName || '…').toUpperCase();
  let hs = L.headSizeMax;
  let t1w, nameW;
  while (true) {
    t1w = measure(t1, hs);
    nameW = measure(nameTxt, hs);
    const blockW = L.heartH * heartAR + L.heartGap + t1w;
    if ((blockW <= usableW && nameW <= usableW) || hs <= L.headSizeMin) break;
    hs -= 1;
  }
  const heartW = L.heartH * heartAR;
  const blockW = heartW + L.heartGap + t1w;
  const bx = (L.W - blockW) / 2;
  out.header.heart = { x: bx, y: L.heartTop, w: heartW, h: L.heartH };
  out.header.line1 = { text: t1, size: hs, textX: bx + heartW + L.heartGap, y: L.line1Y };
  out.header.name = { text: nameTxt, size: hs, x: L.W / 2, y: L.nameY };

  const n = state.members.length;
  if (n === 0) return out;
  const widths = state.members.map((m) => {
    const f = figures[m.type];
    return L.baseFigureH * f.rel * (f.w / f.h);
  });
  const totalW = widths.reduce((a, b) => a + b, 0) + state.gapMm * (n - 1);
  const scale = totalW > usableW ? usableW / totalW : 1;
  const gap = state.gapMm * scale;

  const groupW = totalW * scale;
  let x;
  if (state.align === 'left') x = L.margin;
  else if (state.align === 'right') x = L.W - L.margin - groupW;
  else x = (L.W - groupW) / 2;

  state.members.forEach((m) => {
    const f = figures[m.type];
    const h = L.baseFigureH * f.rel * scale;
    const w = h * (f.w / f.h);
    out.figures.push({
      type: m.type, name: (m.name || '').trim().slice(0, FAMILY_LIMITS.nameMax),
      x, w, h, cx: x + w / 2, topY: L.baselineY - h,
    });
    x += w + gap;
  });

  let fs = L.memberNameSizeMax;
  // Der Name darf breiter sein als sein Element (bis f.w + 90 % der Luecke) —
  // aber er darf die SICHERHEITSZONE nicht verlassen. Ohne die zweite
  // Bedingung lief er bei linksbuendiger Reihe und langem Namen bis auf
  // 7,6 mm an die Mattenkante und bei „WWWWWWWWWW" sogar darueber hinaus
  // (gemessen 31.08.2026 gegen die echte Schrift, Pillow/Druckmassstab).
  // Geprueft wird die aeussere Kante des Namens gegen L.safeInsetMm; steht der
  // Schluessel nicht im Layout, gilt das bisherige Verhalten unveraendert.
  const safe = Number.isFinite(Number(L.safeInsetMm)) ? Number(L.safeInsetMm) : 0;
  const fits = (size) => {
    return out.figures.every((f) => {
      if (!f.name) return true;
      const b = measure(f.name, size);
      if (b > f.w + gap * 0.9) return false;
      return (f.cx - b / 2) >= safe && (f.cx + b / 2) <= L.W - safe;
    });
  };
  while (fs > L.memberNameSizeMin && !fits(fs)) fs -= 1;
  out.nameFont = fs;
  return out;
}

// ---------- Design rendern (Transform: 1 Einheit = 1 mm, Ursprung oben links) ----------
// 1:1-Portierung von renderDesign(ctx) aus dem Prototyp. env:
//   figures, heartAR  — wie oben
//   images   — { mat: Image|null, hearts: {rot,weiss}, figures: {key: Image} }
//   matFallback — Volltonfarbe, wenn kein Hintergrundbild geladen ist
// Gibt das berechnete Layout zurück (Basis für _ttg_family_config).
export function renderFamilyDesign(ctx, state, env) {
  const L = env.L || FAMILY_LAYOUT;
  if (env.images.mat) ctx.drawImage(env.images.mat, 0, 0, L.W, L.H);
  else { ctx.fillStyle = env.matFallback || '#232228'; ctx.fillRect(0, 0, L.W, L.H); }

  const lay = computeFamilyLayout(state, {
    figures: env.figures, heartAR: env.heartAR, L,
    measure: env.measure || makeCtxMeasure(ctx),
  });
  const white = '#FFFFFF';

  const hh = lay.header.heart;
  ctx.drawImage(env.images.hearts[state.heart], hh.x, hh.y, hh.w, hh.h);
  const h1 = lay.header.line1;
  ctx.fillStyle = white;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.font = `${h1.size}px '${FAMILY_FONT}'`;
  ctx.fillText(h1.text, h1.textX, h1.y);

  const nm = lay.header.name;
  ctx.textAlign = 'center';
  ctx.font = `${nm.size}px '${FAMILY_FONT}'`;
  ctx.fillText(nm.text, nm.x, nm.y);

  lay.figures.forEach((f) => {
    ctx.drawImage(env.images.figures[f.type], f.x, f.topY, f.w, f.h);
    if (f.name) {
      ctx.font = `${lay.nameFont}px '${FAMILY_FONT}'`;
      ctx.fillText(f.name, f.cx, L.memberNameY);
    }
  });
  return lay;
}

// ---------- Bühnen-Transform (Rahmen-Overlay) ----------
// 1:1 die Mathematik aus drawPreview() des Prototyps: Design als Cover
// zentriert ins Innenfenster, Überstand liegt unterm Rahmen.
export function computeStageTransform(rahmen, L = FAMILY_LAYOUT) {
  const win = rahmen.fenster;
  const winW = win.x2 - win.x, winH = win.y2 - win.y;
  const s = Math.max(winW / L.W, winH / L.H);
  const dw = L.W * s, dh = L.H * s;
  return {
    winW, winH, s,
    dx: win.x - (dw - winW) / 2,
    dy: win.y - (dh - winH) / 2,
  };
}

// ---------- Varianten-Auflösung (Mattenfarbe × Größe) ----------

// Größen-Schlüssel aus einem Options-Wert ableiten: NUR über die Ziffern
// ("40 × 60 cm" → "40x60"), nie über Stringvergleich — Optionswerte tragen
// im Shop U+202F um das ×, selbst getippte Muster verfehlen sie still
// (CLAUDE.md §7). Der Original-Wert wird für die Auflösung unverändert benutzt.
export function sizeKeyFromOptionValue(value) {
  const m = String(value).match(/(\d+)\D+?(\d+)/);
  return m ? `${m[1]}x${m[2]}` : null;
}

// Farbwort falten (Grün → gruen), NUR um Options-Werte den Katalog-Keys
// zuzuordnen — verglichen wird danach immer mit dem Original-Wert des Produkts.
export function foldColorWord(s) {
  return String(s).toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

// Options-Struktur des Produkts lesen (order-agnostisch, Namen konfigurierbar).
// product: { options: [{name, values[]}], variants: [{id, available, options[]}] }
// Rückgabe: { matIdx, sizeIdx, matValueByKey, matKeyByValue, sizeValueByKey }
export function mapFamilyOptions(product, catalog, optionNames = FAMILY_OPTION_NAMES) {
  if (!product || !Array.isArray(product.options) || !Array.isArray(product.variants)) return null;
  const matIdx = product.options.findIndex((o) => o.name === optionNames.mat);
  const sizeIdx = product.options.findIndex((o) => o.name === optionNames.size);
  if (matIdx < 0 || sizeIdx < 0) return null;

  const matValueByKey = {}, matKeyByValue = {};
  for (const value of product.options[matIdx].values) {
    const folded = foldColorWord(value);
    const key = Object.keys(catalog.hintergruende || {}).find(
      (k) => k === folded || foldColorWord(catalog.hintergruende[k].label) === folded
    );
    if (key) { matValueByKey[key] = value; matKeyByValue[value] = key; }
  }
  const sizeValueByKey = {};
  for (const value of product.options[sizeIdx].values) {
    const key = sizeKeyFromOptionValue(value);
    if (key) sizeValueByKey[key] = value;
  }
  return { matIdx, sizeIdx, matValueByKey, matKeyByValue, sizeValueByKey };
}

// Variante zu (Katalog-Farbkey, Größen-Key) finden. Verglichen wird der
// Options-Wert der VARIANTE gegen den Options-Wert aus der Produktdefinition —
// beides Shopify-Originale, nichts Selbstgetipptes.
export function resolveFamilyVariant(product, catalog, matKey, sizeKey, optionNames = FAMILY_OPTION_NAMES) {
  const map = mapFamilyOptions(product, catalog, optionNames);
  if (!map) return null;
  const matValue = map.matValueByKey[matKey];
  if (matValue == null) return null;
  const v = product.variants.find((it) =>
    it.options[map.matIdx] === matValue && sizeKeyFromOptionValue(it.options[map.sizeIdx]) === sizeKey);
  return v ? {
    id: v.id, available: v.available !== false, matValue, sizeValue: v.options[map.sizeIdx],
    price: v.price != null ? v.price : null,
    compareAtPrice: v.compareAtPrice != null ? v.compareAtPrice : null,
  } : null;
}

// ---------- Cart-Properties (Vertrag Schema v1) ----------
// Sichtbar: Familienname, Familie, Herz — Mattenfarbe/Größe kommen über die
// Variante. Versteckt: _ttg_family_config mit ALLEN berechneten mm-Werten;
// der Druck-Renderer rechnet NICHTS neu, er platziert nur. textAlign je
// Textfeld ist seit 20.08.2026 Teil des Vertrags: line1 "left" (x = linker
// Textanfang), Familienname und Mitgliedsnamen "center" (x bzw. cx = Mitte).
// mm-Werte auf 2 Nachkommastellen gerundet (0,01 mm ≈ 0,12 px bei 300 dpi).
const r2 = (v) => Math.round(v * 100) / 100;

export function buildFamilyProperties(state, lay, figures) {
  const famName = (state.famName || '').trim().slice(0, FAMILY_LIMITS.famNameMax);
  const familie = lay.figures
    .map((f) => (figures[f.type].label + (f.name ? ' ' + f.name : '')))
    .join(' · ');
  const cfg = {
    v: 1,
    size: state.size,
    mat: state.mat,
    heart: state.heart,
    align: state.align,
    gapMm: state.gapMm,
    headSize: lay.header.line1.size,
    famName,
    line1: { text: lay.header.line1.text, x: r2(lay.header.line1.textX), y: lay.header.line1.y, align: 'left' },
    name: { text: lay.header.name.text, x: r2(lay.header.name.x), y: lay.header.name.y, align: 'center' },
    heartBox: { x: r2(lay.header.heart.x), y: lay.header.heart.y, w: r2(lay.header.heart.w), h: lay.header.heart.h },
    members: lay.figures.map((f) => ({
      type: f.type, name: f.name,
      x: r2(f.x), topY: r2(f.topY), w: r2(f.w), h: r2(f.h), cx: r2(f.cx),
      align: 'center',
    })),
    nameFont: lay.nameFont,
    memberNameY: FAMILY_LAYOUT.memberNameY,
  };
  return {
    'Familienname': famName,
    'Familie': familie,
    'Herz': HEART_LABELS[state.heart] || state.heart,
    '_ttg_family_config': JSON.stringify(cfg),
  };
}

// ---------- Größenratgeber (custom.size_table v2, pure) ----------
// Strenger v2-Parser nach dem Muster der Hauptsection, aber bewusst enger:
// version !== 2 → verwerfen; `overrides` → verwerfen (halb gerendert wären
// die Maße FALSCH, nicht bloß unvollständig — die Familien-Matte hat keine
// schnittabhängigen Tabellen); `image` wird ignoriert (Overlay kennt
// note + Tabelle, Reihenfolge wie im Engine-Renderer: note zuerst).
export function parseFamilySizeTable(raw) {
  if (!raw || Array.isArray(raw) || typeof raw !== 'object') return null;
  if (raw.version !== 2) return null;
  if (raw.overrides) return null;
  if (!Array.isArray(raw.columns) || !raw.columns.length) return null;
  if (!Array.isArray(raw.rows) || !raw.rows.length) return null;
  return {
    columns: raw.columns.map(String),
    rows: raw.rows.map((r) => (Array.isArray(r) ? r.map(String) : [String(r)])),
    note: typeof raw.note === 'string' && raw.note ? raw.note : null,
  };
}

// ---------- Namensfeld an der Grenze (pure, Node-testbar) ----------
// Kuerzt auf nameMax (auch beim Einfuegen ueber die Grenze) und meldet, ob der
// Hinweis unter der Zeile stehen soll: genau dann, wenn die Grenze erreicht ist.
export const FAMILY_NAME_LIMIT_HINT = 'Mehr als ' + FAMILY_LIMITS.nameMax + ' Zeichen passen leider nicht unter die Figur.';
export function familyNameInput(raw) {
  const value = String(raw == null ? '' : raw).slice(0, FAMILY_LIMITS.nameMax);
  return { value, atLimit: value.length >= FAMILY_LIMITS.nameMax };
}

// ---------- Kaufbarkeits-Gate + Vorschau-Auftrag (pure, Node-testbar) ----------

// CTA-Regel: Der native Kaufknopf wird NIE deaktiviert — er meldet den
// fehlenden Schritt (TTG-Grundregel „CTA meldet statt disabled"). Diese
// Funktion entscheidet nur; das Melden macht das Element.
// Whitespace zählt als leer (schließt die in Session 2 notierte Lücke).
//
// Zweite Pruefung (26.10.2026, Namen bis 12 Zeichen): Mit { lay, measure }
// meldet das Gate Mitgliedsnamen, die auch in der kleinsten Schrift
// (lay.nameFont am Boden memberNameSizeMin) in die Sicherheitszone
// (FAMILY_LAYOUT.safeInsetMm, 20 mm) laufen. Worst-Case-Messung mit echter
// Schrift (scripts/mess-familie-namen12.mjs): 562 von 1248 Faellen lagen
// darunter — die Schrumpfschleife hoert am Boden auf, auch wenn es nicht passt.
// tooWide = Indizes der betroffenen Mitglieder (Reihenfolge wie state.members).
export const FAMILY_NAME_TOO_WIDE_TEXTS = {
  center: 'Dieser Name ist für die Anordnung zu breit. Stellt die Ausrichtung auf Mittig oder kürzt den Namen.',
  gap: 'Dieser Name ist für die Anordnung zu breit. Vergrößert den Abstand oder kürzt den Namen.',
  both: 'Dieser Name ist für die Anordnung zu breit. Stellt die Ausrichtung auf Mittig, vergrößert den Abstand oder kürzt den Namen.',
  none: 'Dieser Name ist für die Anordnung zu breit. Bitte kürzt ihn.',
};
// Rueckwaertsname: Grundtext, falls keine Rechnung moeglich ist.
export const FAMILY_NAME_TOO_WIDE = FAMILY_NAME_TOO_WIDE_TEXTS.none;
export function familyGateState(state, check) {
  const missing = [];
  if (!state.famName || !state.famName.trim()) missing.push('famName');
  if (!check) return { ok: missing.length === 0, missing };
  const L = check.L || FAMILY_LAYOUT;
  const safe = Number(L.safeInsetMm) || 0;
  const tooWide = [];
  check.lay.figures.forEach((f, i) => {
    if (!f.name) return;
    const b = check.measure(f.name, check.lay.nameFont);
    if (f.cx - b / 2 < safe - 1e-9 || f.cx + b / 2 > L.W - safe + 1e-9) tooWide.push(i);
  });
  if (tooWide.length) missing.push('memberName');
  return { ok: missing.length === 0, missing, tooWide };
}

// Hinweis je zu breitem Namen (26.10.2026): nennt nur, was in GENAU diesem
// Fall hilft. Nachgerechnet mit computeFamilyLayout + familyGateState:
//   center — Ausrichtung Mittig (nur wenn heute nicht mittig)
//   gap    — groesster Abstand FAMILY_LIMITS.gapMax (nur wenn heute kleiner)
// Kuerzen hilft immer. env = { figures, heartAR, measure, L? }.
export function familyTooWideAdvice(state, idx, env) {
  const passt = (st) => {
    const lay = computeFamilyLayout(st, env);
    const g = familyGateState(st, { lay, measure: env.measure, L: env.L });
    return !g.tooWide.includes(idx);
  };
  const center = state.align !== 'center' && passt({ ...state, align: 'center' });
  const gap = state.gapMm < FAMILY_LIMITS.gapMax && passt({ ...state, gapMm: FAMILY_LIMITS.gapMax });
  const key = center && gap ? 'both' : center ? 'center' : gap ? 'gap' : 'none';
  return { center, gap, text: FAMILY_NAME_TOO_WIDE_TEXTS[key] };
}

// Auftrag für den globalen Nachtrag-Weg (window.__ttgPreviewUploader.nachtragen,
// assets/ttg-preview-uploader.js) — dieselbe Form wie der sessionStorage-Stash
// des Theme-Adapters. null, wenn der Upload aus ist oder kein Bild vorliegt.
export function buildPreviewJob(group, dataUrl, previewUpload) {
  const pu = previewUpload || {};
  if (!pu.enabled || !pu.url || !group || !dataUrl) return null;
  return {
    group,
    dataUrl,
    url: pu.url,
    property: pu.property || '_preview_image',
    maxUploadBytes: pu.maxUploadBytes || 512000,
  };
}

// Gruppen-ID exakt wie ttg-theme-adapter.js:245 — die ersten 8 Zeichen nach
// dem 'g' sind die Add-Zeit (Unix-ms, Base36); Diagnose-Vertrag im
// Systemhandbuch („_ttg_group kodiert die Add-to-Cart-Zeit").
export function newFamilyGroupId(now = Date.now(), rand = Math.random()) {
  return 'g' + now.toString(36) + Math.floor(rand * 1e6).toString(36);
}

// ---------- Custom Element (nur Browser) ----------

const DEFAULT_STATE = {
  famName: 'Müller',
  members: [
    { type: 'vater', name: 'Thomas' },
    { type: 'mutter', name: 'Steffi' },
    { type: 'junge', name: 'Ben' },
    { type: 'hund', name: 'Balu' },
  ],
  mat: 'schwarz',
  heart: 'rot',
  align: 'center',
  size: '40x60',
  gapMm: FAMILY_LIMITS.gapDefault,
};

function loadImage(url, crossOrigin, prio = 'auto') {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    // Priorität je Bild (offene Punkte 08.10.2026, Teil C): 'high' nur für das Anzeige-Hintergrundbild (größte Datei
    // vor dem Aufbau), 'low' für das Exportbild nach dem Aufbau, sonst Browser-Standard.
    if (prio !== 'auto') img.fetchPriority = prio;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Bild nicht ladbar: ' + url));
    img.src = url;
  });
}

// Klassendefinition im Browser-Guard: die Datei muss ohne DOM-Globals
// Node-importierbar bleiben (test/family-layout.mjs) — gleiche Trennung wie
// Engine (Node-fähig) vs. Theme-Adapter (Browser), nur innerhalb einer Datei.
function defineFamilyElement() {
  // Gleiche Selektoren wie ttg-theme-adapter.js (Cart-Handover Variante B).
  const FORM_SELECTOR = 'product-form-component form[data-type="add-to-cart-form"]';

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  class TTGFamilyCustomizer extends HTMLElement {
  connectedCallback() {
    if (this._booted) {
      // Wiedereintritt (Theme-Editor verschiebt Sections): nicht neu booten,
      // aber die vom disconnect entfernte Body-Klasse wieder setzen.
      if (this.lastLayout) document.body.classList.add('ttg-family-active');
      return;
    }
    this._booted = true;
    this._imgs = { hearts: {}, figures: {}, mats: {}, matsDruck: {}, rahmen: null };
    this._raf = 0;
    this.init().catch((e) => {
      console.error('[ttg-family] Start fehlgeschlagen:', e);
      // CLS-Rückfall (custom.css F5, 07.10.2026): native Produktseite sofort freigeben.
      this.setAttribute('data-ttg-failed', 'init');
      this._note(this._strings().load_error || 'Vorschau konnte nicht geladen werden.');
    });
  }

  _config() {
    if (!this._cfg) {
      const cfgEl = this.querySelector('script[type="application/json"][data-ttg-family-config]');
      this._cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {};
    }
    return this._cfg;
  }

  _strings() { return this._config().strings || {}; }

  _note(text) {
    const noteEl = this.querySelector('[data-ttg-family-note]');
    if (noteEl) noteEl.textContent = text;
  }

  _form() {
    return document.querySelector(FORM_SELECTOR)
      || document.querySelector('product-form-component form');
  }

  async init() {
    const cfg = this._config();
    this.state = {
      ...DEFAULT_STATE,
      ...(cfg.defaultState || {}),
      members: ((cfg.defaultState && cfg.defaultState.members) || DEFAULT_STATE.members).map((m) => ({ ...m })),
    };

    const res = await fetch(cfg.catalogUrl);
    if (!res.ok) throw new Error('Katalog HTTP ' + res.status);
    this.catalog = await res.json();
    this.figures = buildFigureMap(this.catalog);
    this.heartAR = heartAspect(this.catalog);

    // Produktdaten (Optionen/Varianten) — vom Liquid emittiert; auf der
    // Sichtprobe ein Mock. Startzustand folgt der vorausgewählten Variante.
    this.product = cfg.product || null;
    this.optionNames = { ...FAMILY_OPTION_NAMES, ...(cfg.optionNames || {}) };
    this.optMap = this.product ? mapFamilyOptions(this.product, this.catalog, this.optionNames) : null;
    if (this.product && !this.optMap) {
      console.warn('[ttg-family] Produktoptionen passen nicht zu', this.optionNames, '— Varianten-Sync aus.');
    }
    this._applyInitialVariant();

    // Cabin Sketch VOR dem ersten Canvas-Rendern laden und awaiten — PARALLEL zu den Bildern (CLS-Runde 2, 08.10.2026:
    // vorher erst Schrift, dann Bilder; gedrosselt kostete das 2,7–4,5 s bis zum Aufbau).
    // BEWUSST OHNE format-Hint (21.08.2026): Die CDN-Datei heißt .ttf, der
    // Inhalt ist per Magic-Bytes gemessen woff2 — ein Hint, der je nach
    // Dateitausch falsch wird, kann den Load kosten. Ohne Hint sniffen die
    // Browser das echte Format, egal was das CDN gerade liefert.
    const schrift = (async () => {
      if (!cfg.fontUrl || typeof FontFace === 'undefined') return;
      try {
        const face = new FontFace(FAMILY_FONT, `url(${cfg.fontUrl})`);
        await face.load();
        document.fonts.add(face);
      } catch (e) {
        console.warn('[ttg-family] Cabin Sketch nicht ladbar, Fallback-Schrift aktiv:', e);
      }
    })();

    await Promise.all([schrift, this._ensureImages()]);
    this._canvas = this.querySelector('canvas[data-ttg-family-preview]');
    this.sizeTable = parseFamilySizeTable(cfg.sizeTable);
    if (cfg.sizeTable && !this.sizeTable) {
      console.warn('[ttg-family] size_table verworfen (version != 2, overrides oder columns/rows fehlen) — kein Größenratgeber.');
    }
    this.previewUpload = cfg.previewUpload || null;
    if (!this.previewUpload || !this.previewUpload.enabled) {
      // Nicht still (Lehre aus customizer_settings): der Ausfall der
      // Druckvorlagen-Vorschau soll in der Konsole sichtbar sein.
      console.info('[ttg-family] Vorschau-Upload aus (customizer_settings.previewUpload fehlt oder disabled).');
    }
    this._buildUI();
    this._armSubmitGate();
    this._setupGallery();
    this._adoptInfoBlocks();
    this._setupMini();
    window.addEventListener('resize', () => this._scheduleDraw());
    this._touch();
    this._note('');
    // Erst nach nachweislichem Boot: native Doppel-UI (Variantenwähler,
    // Galerie) weicht per CSS. Fällt der Boot aus, bleibt die Seite
    // vollständig nativ und kaufbar (fail-open wie ttg-theme-adapter).
    document.body.classList.add('ttg-family-active');
    this._kopfNachOben();
    this._ladeDruckBild().catch(() => {});
  }

  // Exportbild der aktuellen Mattenfarbe für das Vorschaubild: dieselbe 1600er-Rendition wie bis 08.10.2026 (Bytes des
  // Vorschaubilds unverändert), erst nach dem Aufbau bzw. nach dem Anzeigebild einer neuen Farbe, mit niedriger Priorität
  // (M2). Liefert das laufende Laden als Promise (der Klick-Fänger wartet darauf); nach einem Fehlschlag startet der nächste
  // Aufruf neu. Schlüssel beim Start binden (Wiese/Kork-Falle).
  _ladeDruckBild() {
    const matKey = this.state.mat;
    const mat = (this.catalog.hintergruende || {})[matKey];
    this._druckLaedt = this._druckLaedt || {};
    if (!mat || !mat.url) return Promise.reject(new Error('kein Mattenbild'));
    if (this._imgs.matsDruck[matKey]) return Promise.resolve(this._imgs.matsDruck[matKey]);
    if (!this._druckLaedt[matKey]) {
      this._druckLaedt[matKey] = loadImage(cdnSize(mat.url, 1600), true, 'low')
        .then((i) => { this._imgs.matsDruck[matKey] = i; return i; }, (e) => { this._druckLaedt[matKey] = null; throw e; });
      this._druckLaedt[matKey].catch(() => {});
    }
    return this._druckLaedt[matKey];
  }

  // Fehlt beim Kauf das Exportbild der gewählten Farbe? Nur relevant, wenn ein Vorschaubild hochgeladen wird.
  _exportFehlt() {
    const mat = (this.catalog.hintergruende || {})[this.state.mat];
    return !!(this.previewUpload && this.previewUpload.enabled && mat && mat.url && !this._imgs.matsDruck[this.state.mat]);
  }

  // M1 (Runde 3, 08.10.2026): Kaufklick, bevor das Exportbild da ist → Kauf anhalten, Knopf zeigt „Vorschau wird
  // erstellt…“, bis FAMILY_EXPORT_WARTEN_MS auf das Bild warten (Fehlschlag → neuer Versuch), dann denselben Kauf
  // erneut auslösen. Frist abgelaufen → sichtbare Meldung unter dem Knopf, kein Add-to-Cart.
  _aufExportWarten(btn, weiter) {
    this._exportWartet = true;
    const eigen = !!(btn && btn.hasAttribute('data-ttg-family-add-btn'));
    const text = eigen ? btn.textContent : null;
    if (eigen) btn.textContent = this._strings().preview_wait || 'Vorschau wird erstellt…';
    if (btn) btn.setAttribute('aria-busy', 'true');
    this._setExportMeldung('');
    const bis = Date.now() + FAMILY_EXPORT_WARTEN_MS;
    const versuch = () => this._ladeDruckBild().then(() => true, () => (Date.now() + 1000 < bis
      ? new Promise((r) => setTimeout(r, 1000)).then(versuch) : false));
    let uhr = null;
    const frist = new Promise((r) => { uhr = setTimeout(() => r(false), Math.max(0, bis - Date.now())); });
    Promise.race([versuch(), frist]).then((ok) => {
      clearTimeout(uhr);
      if (eigen) btn.textContent = text;
      if (btn) btn.removeAttribute('aria-busy');
      this._exportWartet = false;
      if (ok && !this._exportFehlt()) { weiter(); return; }
      this._setExportMeldung(this._strings().preview_timeout
        || 'Die Vorschau für Eure Bestellung konnte gerade nicht erstellt werden. Bitte prüft die Verbindung und tippt noch einmal auf „In den Warenkorb“.');
    });
  }

  _setExportMeldung(text) {
    let m = this.querySelector('[data-ttg-family-export-meldung]');
    if (!m && !text) return;
    if (!m) {
      const ctaBtn = this.querySelector('[data-ttg-family-add-btn]');
      if (!ctaBtn) { this._setGateError(text); return; }
      m = el('p', 'ttg-family__error');
      m.setAttribute('data-ttg-family-export-meldung', '');
      m.setAttribute('role', 'alert');
      ctaBtn.insertAdjacentElement('afterend', m);
    }
    m.textContent = text; m.hidden = !text;
  }

  disconnectedCallback() {
    document.body.classList.remove('ttg-family-active');
  }

  // ---------- Kaufweg: Gate + _ttg_group + Vorschau-Nachtrag ----------

  // Klick-Fänger in der Capture-Phase (läuft vor den Theme-Handlern des
  // nativen product-form-component) + submit-Fallback für requestSubmit/Enter.
  _armSubmitGate() {
    const form = this._form();
    if (!form || form.__ttgFamilyArmed) return;
    form.__ttgFamilyArmed = true;

    // Kaufknopf UNTER der Konfiguration: submittet das native Formular per
    // form=-Attribut (Variante B); der native Kaufweg bleibt intakt, seine
    // Buttons versteckt nur das CSS (body.ttg-family-active).
    const ctaBtn = this.querySelector('[data-ttg-family-add-btn]');
    const ctaWrap = this.querySelector('[data-ttg-family-cta]');
    if (ctaBtn && ctaWrap) {
      // DOM-Clobbering-Falle (Dev-Messung 20.08.2026): form.id liefert hier
      // das <input name="id"> (der Varianten-Input verschattet die Eigenschaft),
      // nicht das id-Attribut — deshalb zwingend getAttribute/setAttribute.
      let formId = form.getAttribute('id');
      if (!formId) { formId = 'ttg-family-native-form'; form.setAttribute('id', formId); }
      ctaBtn.setAttribute('form', formId);
      ctaWrap.hidden = false;
    }
    // WICHTIG — genau EIN _prepareAdd je Kaufvorgang (Dev-E2E 20.08.2026):
    // Der Theme-Listener des product-form-component ist VOR unserem
    // submit-Listener registriert und läuft am Target zuerst — er baut die
    // FormData, BEVOR unser submit-Fallback dran ist. Ein zweites
    // _prepareAdd dort erzeugte eine NEUE _ttg_group nur für den
    // Upload-Auftrag: die Warenkorbzeile trug Gruppe A, der Nachtrag suchte
    // Gruppe B — das Vorschaubild ging still verloren. Deshalb bereitet der
    // Klick-Fänger vor (läuft vor der Default-Action) und der
    // submit-Fallback NUR, wenn kein Klick vorausging (Enter/requestSubmit).
    document.addEventListener('click', (e) => {
      const btn = e.target && e.target.closest
        ? e.target.closest('[ref="addToCartButton"], button[name="add"], button[type="submit"], input[type="submit"]')
        : null;
      // form.contains: native Buttons IM Formular; btn.form: unser externer
      // CTA, der per form=-Attribut ans Formular gebunden ist.
      if (!btn || !(form.contains(btn) || btn.form === form)) return;
      if (this._exportWartet) { e.preventDefault(); e.stopPropagation(); return; }
      if (!this._gate(true)) { e.preventDefault(); e.stopPropagation(); return; }
      if (this._exportFehlt()) { e.preventDefault(); e.stopPropagation(); this._aufExportWarten(btn, () => btn.click()); return; }
      this._setExportMeldung('');
      this._prepareAdd(form);
      this._addPrepared = true;
      setTimeout(() => { this._addPrepared = false; }, 0);
    }, true);
    form.addEventListener('submit', (e) => {
      if (this._exportWartet) { e.preventDefault(); e.stopImmediatePropagation(); return; }
      if (!this._gate(true)) { e.preventDefault(); e.stopImmediatePropagation(); return; }
      if (!this._addPrepared && this._exportFehlt()) {
        e.preventDefault(); e.stopImmediatePropagation();
        const sub = e.submitter || null;
        this._aufExportWarten(sub, () => form.requestSubmit(sub || undefined));
        return;
      }
      if (!this._addPrepared) this._prepareAdd(form);
    }, true);
    document.addEventListener('cart:update', () => this._afterAdd());
    document.addEventListener('cart:error', () => { this._pendingPreview = null; });
  }

  // CTA-Regel: nicht deaktivieren, sondern melden — Fehltext am Feld,
  // Fokus + Scroll. Rückgabe: darf gekauft werden?
  _gate(showError) {
    const gate = this._canvas
      ? familyGateState(this.state, {
        lay: computeFamilyLayout(this.state, { figures: this.figures, heartAR: this.heartAR, measure: makeCtxMeasure(this._canvas.getContext('2d')) }),
        measure: makeCtxMeasure(this._canvas.getContext('2d')),
      })
      : familyGateState(this.state);
    this._showTooWide(showError ? (gate.tooWide || []) : (this._tooWideShown ? (gate.tooWide || []) : []),
      this._canvas ? { figures: this.figures, heartAR: this.heartAR, measure: makeCtxMeasure(this._canvas.getContext('2d')) } : null);
    if (gate.ok) { this._setGateError(''); return true; }
    if (showError && !gate.missing.includes('famName') && gate.tooWide && gate.tooWide.length) {
      const row = this._tooWideEls && this._tooWideEls[gate.tooWide[0]];
      const inp = row && row.input;
      if (inp) {
        try { inp.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { /* alte Browser */ }
        inp.focus({ preventScroll: true });
      }
      return false;
    }
    if (showError) {
      this._setGateError(this._strings().missing_famname || 'Bitte gebt zuerst euren Familiennamen ein.');
      const inp = this.querySelector('#ttg-family-famname');
      if (inp) {
        inp.classList.add('ttg-family__input--error');
        try { inp.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { /* alte Browser */ }
        inp.focus({ preventScroll: true });
      }
    }
    return false;
  }

  // Meldung am betroffenen Namen (CTA-Regel: melden statt deaktivieren). Bleibt
  // nach dem ersten Kaufklick live und verschwindet, sobald der Name passt.
  _showTooWide(idx, env) {
    this._tooWideShown = idx.length > 0;
    (this._tooWideEls || []).forEach((r, i) => {
      const an = idx.includes(i);
      if (an) r.msg.textContent = env ? familyTooWideAdvice(this.state, i, env).text : FAMILY_NAME_TOO_WIDE;
      r.msg.hidden = !an;
      r.input.classList.toggle('ttg-family__input--error', an);
    });
  }

  _setGateError(text) {
    const el2 = this.querySelector('[data-ttg-family-error]');
    if (el2) { el2.textContent = text; el2.hidden = !text; }
    if (!text) {
      const inp = this.querySelector('#ttg-family-famname');
      if (inp) inp.classList.remove('ttg-family__input--error');
    }
  }

  // Unmittelbar vor dem nativen Add: Properties final syncen, _ttg_group
  // setzen (Cart-Gruppierung + Anker für den Vorschau-Nachtrag) und den
  // Snapshot festhalten. Alles synchron — die Bilder sind bereits geladen.
  _prepareAdd(form) {
    this._syncCart();
    const group = newFamilyGroupId();
    form.querySelectorAll('input[data-ttg-family-group]').forEach((n) => n.remove());
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_ttg_group]';
    input.value = group;
    input.setAttribute('data-ttg-family-group', '');
    form.appendChild(input);

    let dataUrl = null;
    if (this.previewUpload && this.previewUpload.enabled) {
      try { dataUrl = this._printSnapshotDataUrl(); }
      catch (e) { console.warn('[ttg-family] Snapshot fehlgeschlagen — Add läuft ohne Vorschaubild:', e); }
    }
    this._pendingPreview = buildPreviewJob(group, dataUrl, this.previewUpload);
  }

  // Nach erfolgreichem Add (cart:update): Nachtrag über den GLOBAL geladenen
  // Uploader (assets/ttg-preview-uploader.js via theme.liquid) — gebaut für
  // Aufrufer ohne Navigation; Upload 3×, Anhängen 3×, nie überschreiben.
  // Nach erfolgreichem Add (cart:update): Vorschaubild sichern, dann zur
  // Warenkorbseite (Wunsch Jens 21.08.2026 — vorher blieb der Kunde ohne
  // sichtbare Rueckmeldung auf der Produktseite; eine Warenkorb-Schublade
  // hat das Theme nicht, cart_type steht auf "page", live gemessen).
  //
  // DIE GEFAHR: Der Vorschaubild-Nachtrag ist eine KETTE aus zwei abhaengigen
  // Schritten — erst das Bild hochladen (die Antwort liefert die Bild-URL),
  // dann /cart.js lesen, die Zeile finden und per /cart/change.js die
  // Eigenschaft _preview_image anhaengen. Eine sofortige Weiterleitung
  // schneidet diese Kette mittendrin ab; die Bestellung haette dann keine
  // Druckvorlage. Deshalb wird nicht einfach navigiert.
  //
  // WARUM NICHT keepalive/sendBeacon (im Briefing als Kandidat genannt):
  // Beide taugen hier nicht, aus zwei unabhaengigen Gruenden.
  //   1. Sie sind "abschicken und vergessen" — die ANTWORT ist nicht lesbar.
  //      Schritt 2 braucht aber die Bild-URL aus Schritt 1 und die Zeilen-Id
  //      aus /cart.js. Eine Kette laesst sich damit nicht ausfuehren.
  //   2. keepalive deckelt den Rumpf auf 64 KB; unser Snapshot darf bis
  //      maxUploadBytes = 512.000 Bytes gross sein, also das Achtfache.
  //
  // DER WEG, DER TRAEGT: sessionStorage-Stash. Der Uploader hat ihn bereits
  // fuer genau diesen Ablauf ("Artikel in den Warenkorb, danach navigiert das
  // Theme zur /cart-Seite"); sein boot() nimmt den Auftrag dort auf, sobald
  // eine Cart-Oberflaeche da ist. Die grosse Engine (ttg-theme-adapter.js) und
  // der Cross-Sell nutzen ihn seit Langem so. Der Auftrag ueberlebt die
  // Navigation also, statt von ihr abgeschnitten zu werden — es gibt damit
  // gar keine Wartezeit fuer den Kunden und kein Zeitfenster zum Verlieren.
  _afterAdd() {
    const job = this._pendingPreview;
    this._pendingPreview = null;
    if (!job) { this._zumWarenkorb(); return; }

    // Der Stash ist ein EINZELSLOT. Liegt schon ein fremder Auftrag darin
    // (Cross-Sell), wird er nicht ueberschrieben — sonst ginge dessen
    // Vorschaubild verloren. Dann nimmt unserer den Direktweg.
    let gestasht = false;
    try {
      // Seit 13.10.2026: Liste je Gruppe ueber den Uploader (kein Verdraengen mehr);
      // ohne Uploader der alte Einzelslot, der nichts Fremdes ueberschreibt.
      const up = typeof window !== 'undefined' ? window.__ttgPreviewUploader : null;
      if (up && typeof up.stash === 'function') {
        gestasht = up.stash(job) === true;
      } else if (!sessionStorage.getItem(FAMILY_PREVIEW_STASH)) {
        sessionStorage.setItem(FAMILY_PREVIEW_STASH, JSON.stringify(job));
        gestasht = sessionStorage.getItem(FAMILY_PREVIEW_STASH) !== null;
      }
    } catch (e) {
      // Privater Modus / Speicher voll: kein Stash moeglich.
      gestasht = false;
    }
    if (gestasht) { this._zumWarenkorb(); return; }

    // FALLBACK ohne Stash: hier MUSS gewartet werden, sonst schneidet die
    // Navigation die Kette ab. Aber nie endlos — nach der Obergrenze wird
    // trotzdem weitergeleitet und der Fehlschlag protokolliert. Der Kauf
    // bleibt gueltig, nur das Vorschaubild fehlt (Produktbild-Fallback im
    // Cart-Template greift).
    const up = window.__ttgPreviewUploader;
    if (!up || typeof up.nachtragen !== 'function') {
      console.warn('[ttg-family] ttg-preview-uploader nicht geladen — kein Vorschaubild-Nachtrag.');
      this._zumWarenkorb();
      return;
    }
    let fertig = false;
    const lauf = Promise.resolve(up.nachtragen(job))
      .then(() => { fertig = true; })
      .catch((e) => { console.warn('[ttg-family] Vorschaubild-Nachtrag fehlgeschlagen:', e); });
    const frist = new Promise((r) => setTimeout(r, FAMILY_PREVIEW_TIMEOUT_MS));
    Promise.race([lauf, frist]).then(() => {
      if (!fertig) {
        console.warn('[ttg-family] Vorschaubild-Nachtrag nicht binnen '
          + FAMILY_PREVIEW_TIMEOUT_MS + ' ms fertig — es wird ohne Vorschaubild weitergeleitet.');
      }
      this._zumWarenkorb();
    });
  }

  // Weiterleitung an die Warenkorbseite. Route aus dem Theme, damit sie auch
  // unter einem abweichenden Pfad stimmt; replace() statt href, damit der
  // Zurueck-Knopf nicht auf die Produktseite mit bereits gefuelltem Warenkorb
  // zurueckfaellt und zum zweiten Add einlaedt.
  _zumWarenkorb() {
    if (this._weitergeleitet) return;      // cart:update kann mehrfach feuern
    this._weitergeleitet = true;
    const ziel = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
    window.location.replace(ziel);
  }

  // Warenkorb-Vorschaubild (Fix 21.08.2026): QUADRATISCH und über DENSELBEN
  // Renderpfad wie die Bühne (Rahmen + Fenster-Clipping) — das Cart-Template
  // stellt Zeilen mit _preview_image in einer festen 1:1-Kachel dar
  // (cart-products.liquid: ratio = 1) und schnitt den früheren 3:2-Snapshot
  // je Seite um 16,7 % zu („FAMILIE" → „FAM"). Die Engine liefert aus
  // demselben Grund quadratisch (_renderSnapshot). Stage-Ansicht mittig im
  // weißen Quadrat = WYSIWYG-identisch zur abgenommenen Bühne, unbeschnitten.
  _printSnapshotDataUrl() {
    const pu = this.previewUpload || {};
    const w = pu.maxWidth || 1000;
    const off = document.createElement('canvas');
    off.width = w; off.height = w;
    const ctx = off.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, w);
    const mat = (this.catalog.hintergruende || {})[this.state.mat] || {};
    const env = {
      figures: this.figures,
      heartAR: this.heartAR,
      // Teil C/M1: nur das Exportbild 1600 der aktuellen Farbe — nie die Anzeige-Fassung (Prüfung direkt darunter)
      images: { mat: this._imgs.matsDruck[this.state.mat] || null, hearts: this._imgs.hearts, figures: this._imgs.figures },
      matFallback: mat.fallback,
    };
    if (mat.url && !env.images.mat) throw new Error('Exportbild fehlt');
    const rahmen = this.catalog.rahmen;
    if (rahmen && this._imgs.rahmen) {
      const k = w / rahmen.w;
      const hs = Math.round(rahmen.h * k);
      const oy = Math.round((w - hs) / 2);
      ctx.save();
      ctx.translate(0, oy);
      ctx.scale(k, k);
      const t = computeStageTransform(rahmen);
      const win = rahmen.fenster;
      ctx.save();
      ctx.beginPath(); ctx.rect(win.x, win.y, t.winW, t.winH); ctx.clip();
      ctx.translate(t.dx, t.dy); ctx.scale(t.s, t.s);
      renderFamilyDesign(ctx, this.state, env);
      ctx.restore();
      ctx.drawImage(this._imgs.rahmen, 0, 0, rahmen.w, rahmen.h);
      ctx.restore();
    } else {
      // Fallback ohne Rahmen: reines Design contain-zentriert im Quadrat
      const hs = Math.round(w * FAMILY_LAYOUT.H / FAMILY_LAYOUT.W);
      const oy = Math.round((w - hs) / 2);
      ctx.save();
      ctx.translate(0, oy);
      ctx.scale(w / FAMILY_LAYOUT.W, hs / FAMILY_LAYOUT.H);
      renderFamilyDesign(ctx, this.state, env);
      ctx.restore();
    }
    return off.toDataURL(pu.format || 'image/jpeg', pu.quality != null ? pu.quality : 0.85);
  }

  // Startzustand (mat/size) aus der vorausgewählten Variante des Produkts.
  _applyInitialVariant() {
    if (!this.optMap || !this.product.initialVariantId) return;
    const v = this.product.variants.find((it) => String(it.id) === String(this.product.initialVariantId));
    if (!v) return;
    const matKey = this.optMap.matKeyByValue[v.options[this.optMap.matIdx]];
    const sizeKey = sizeKeyFromOptionValue(v.options[this.optMap.sizeIdx]);
    if (matKey) this.state.mat = matKey;
    if (sizeKey && FAMILY_PRINT_SIZES[sizeKey]) this.state.size = sizeKey;
  }

  // Lädt Rahmen, Herzen, die Figuren der aktuellen Mitglieder und den
  // aktuellen Matten-Hintergrund; Hintergrund-/Rahmen-Fehler sind nicht
  // fatal (Vollton- bzw. rahmenlose Vorschau).
  // Alle Vorschau-Bilder als CDN-Renditions (Perf-Fix 20.08.2026): Matte 1600,
  // Rahmen 1600, Figuren 800, Herzen 400 — reicht für Bühne (≤ ~1400 px) und
  // Vorschau-Snapshot (1000 px). Die Roh-URLs bleiben dem Druck-Renderer.
  async _ensureImages() {
    const jobs = [];
    if (this.catalog.rahmen && !this._imgs.rahmen && !this._rahmenFailed) {
      jobs.push(loadImage(cdnSize(this.catalog.rahmen.url, 1600), true)
        .then((i) => { this._imgs.rahmen = i; })
        .catch(() => { this._rahmenFailed = true; }));
    }
    for (const k of Object.keys(this.catalog.heart)) {
      if (!this._imgs.hearts[k]) jobs.push(loadImage(cdnSize(this.catalog.heart[k].url, 400), true).then((i) => { this._imgs.hearts[k] = i; }));
    }
    for (const m of this.state.members) {
      // Typ ebenfalls beim Start binden: das Figur-Dropdown MUTIERT genau
      // dieses Mitglieds-Objekt (m.type = sel.value), ein Wechsel während
      // des Downloads würde das Bild sonst unter dem neuen Typ ablegen —
      // dieselbe Verwechslungs-Klasse wie bei der Mattenfarbe.
      const typ = m.type;
      const f = this.figures[typ];
      if (f && !this._imgs.figures[typ]) jobs.push(loadImage(cdnSize(f.url, 800), true).then((i) => { this._imgs.figures[typ] = i; }));
    }
    // Schlüssel BEIM START festhalten, nie im Callback aus dem State lesen
    // (Bugfix 21.08.2026, an Wiese/Kork aufgefallen): Wer während des
    // Downloads weiterklickt, hat beim Eintreffen einen anderen state.mat —
    // das gerade geladene Bild landete dann unter dem NEUEN Schlüssel. Real
    // gemessen: unter `wiese` lag das Blau-Bild. Folge wäre die falsche
    // Mattenfarbe in Vorschau UND Vorschaubild (Druckvorlagen-Referenz),
    // während die Cart-Property `mat` korrekt bleibt — ein stiller
    // Widerspruch, der erst in der Reklamation auffällt.
    const matKey = this.state.mat;
    const mat = (this.catalog.hintergruende || {})[matKey];
    if (mat && !this._imgs.mats[matKey]) {
      const buehne = this.querySelector('canvas[data-ttg-family-preview]');
      const cssW = (buehne && buehne.parentElement && buehne.parentElement.clientWidth) || this.clientWidth;
      const rf = this.catalog.rahmen, anteil = rf && rf.fenster && rf.w ? (rf.fenster.x2 - rf.fenster.x) / rf.w : 1;
      jobs.push(loadImage(cdnSize(mat.url, anzeigeBreite(cssW, window.devicePixelRatio, anteil)), true, 'high').then((i) => { this._imgs.mats[matKey] = i; }).catch(() => { /* Vollton-Fallback */ }));
    }
    await Promise.all(jobs);
  }

  _scheduleDraw() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => { this._raf = 0; this.drawPreview(); });
  }

  drawPreview() {
    const canvas = this._canvas;
    if (!canvas || !canvas.parentElement) return;
    const cssW = canvas.parentElement.clientWidth;
    if (!cssW) {
      // Boot-Rennen (Mobile-Befund 21.08.2026): beim ersten Draw kann das
      // Layout noch 0 breit sein — ohne Retry bliebe der Canvas dauerhaft
      // leer (300×150-Default), der Kunde sieht „gar kein Vorschaubild".
      if ((this._drawRetries || 0) < 120) {
        this._drawRetries = (this._drawRetries || 0) + 1;
        requestAnimationFrame(() => this.drawPreview());
      }
      return;
    }
    this._drawRetries = 0;
    const dpr = window.devicePixelRatio || 1;
    const ctx0 = canvas.getContext('2d');
    const rahmen = this.catalog.rahmen;
    const frameImg = this._imgs.rahmen;

    const mat = (this.catalog.hintergruende || {})[this.state.mat] || {};
    const env = {
      figures: this.figures,
      heartAR: this.heartAR,
      images: { mat: this._imgs.mats[this.state.mat] || null, hearts: this._imgs.hearts, figures: this._imgs.figures },
      matFallback: mat.fallback,
    };

    if (rahmen && frameImg) {
      // Vorschau = Design im Innenfenster + Rahmen obendrauf (Prototyp drawPreview)
      const k = (cssW * dpr) / rahmen.w;
      canvas.width = Math.round(rahmen.w * k);
      canvas.height = Math.round(rahmen.h * k);
      canvas.style.height = (canvas.height / dpr) + 'px';
      ctx0.setTransform(k, 0, 0, k, 0, 0);
      const t = computeStageTransform(rahmen);
      const win = rahmen.fenster;
      ctx0.save();
      ctx0.beginPath(); ctx0.rect(win.x, win.y, t.winW, t.winH); ctx0.clip();
      ctx0.translate(t.dx, t.dy); ctx0.scale(t.s, t.s);
      this.lastLayout = renderFamilyDesign(ctx0, this.state, env);
      ctx0.restore();
      ctx0.drawImage(frameImg, 0, 0, rahmen.w, rahmen.h);
    } else {
      // Fallback: rahmenlose 600×400-Vorschau
      const L = FAMILY_LAYOUT;
      const k = (cssW * dpr) / L.W;
      canvas.width = Math.round(L.W * k);
      canvas.height = Math.round(L.H * k);
      canvas.style.height = (canvas.height / dpr) + 'px';
      ctx0.setTransform(k, 0, 0, k, 0, 0);
      this.lastLayout = renderFamilyDesign(ctx0, this.state, env);
    }
    this._blitMini();
  }

  // Jede Zustandsänderung: Formular SOFORT synchronisieren, Zeichnung per rAF.
  // Bewusst entkoppelt — rAF feuert in verdeckten Tabs/Panes nicht, der
  // Warenkorb-Zustand darf davon nie abhängen (Befund Sichtprobe 20.08.2026).
  // Zusätzlich (Engine-Verhalten): jede Konfig-Änderung schaltet die Bühne
  // von einem evtl. angezeigten Produktfoto zurück auf die Live-Vorschau.
  _touch() {
    this._showLive();
    this._syncCart();
    this._scheduleDraw();
  }

  // Info-Blöcke des main-Abschnitts UNTER den CTA ziehen (Layout-Angleichung
  // 21.08.2026): Kartenzahlungs-Icons → ✔-Textzeilen (Top-Level-group-Blocks)
  // → Accordion „Produktinformationen". DOM-Umzug statt Template-Umbau —
  // minimaler Eingriff, Judge.me und alle Block-Einstellungen unangetastet;
  // fällt das JS aus, bleibt die native Reihenfolge stehen (fail-open).
  // Custom Elements/Listener überleben appendChild (kein Re-Init nötig).
  _adoptInfoBlocks() {
    const ziel = this.querySelector('[data-ttg-family-info]');
    const details = document.querySelector('.product-information .product-details');
    if (!ziel || !details) return;
    try {
      const zuege = [];
      const pay = details.querySelector('.payment-icons');
      if (pay) zuege.push(pay);
      // ✔-Zeilen: am INHALT verankert (Dev-Befund 21.08.2026: der
      // _product-details-Block rendert SELBST als .group-block — ein
      // Struktur-Anker zog den kompletten Details-Wrapper samt Titel um).
      // Vom ✔-Text aufwärts die höchste Gruppe nehmen, die noch KEINE
      // fremden Blöcke (Zahlarten/Picker/Accordion/Titel) enthält.
      const check = [...details.querySelectorAll('.text-block')].find((n) => (n.textContent || '').includes('✔'));
      let gruppe = check ? check.closest('.group-block') : null;
      while (gruppe) {
        const p = gruppe.parentElement ? gruppe.parentElement.closest('.group-block') : null;
        if (p && !p.querySelector('.payment-icons') && !p.querySelector('variant-picker')
          && !p.querySelector('.accordion') && !p.querySelector('h1')) { gruppe = p; continue; }
        break;
      }
      if (gruppe) zuege.push(gruppe);
      const acc = details.querySelector('.accordion');
      if (acc) zuege.push(acc);
      for (const n of zuege) ziel.appendChild(n);
      if (zuege.length) ziel.hidden = false;
    } catch (e) {
      console.warn('[ttg-family] Info-Blöcke nicht umgezogen (native Reihenfolge bleibt):', e);
    }
  }

  // ---------- Desktop-Lage wie Baukasten (23.10.2026, Vorgabe Jens) ----------
  // 1:1 aus ttg-baukasten.js _kopfNachOben (fm8-fix5 1c): Titel (einzige H1), Preis, Bewertung und Highlights des Theme-Kopfs
  // wandern auf dem Desktop oben in die rechte Spalte dieser Section, die Bühne steht damit auf Höhe des Titels. Formular und
  // Variantenwahl bleiben im main-Block (Kaufweg unverändert, Block per CSS aus). Breakpoint 861 px = ab hier ist
  // .ttg-family__grid zweispaltig; schmaler alles zurück an den alten Platz (Handy wie bisher).
  _kopfNachOben() {
    const inhalt = document.querySelector('.product-information__grid .product-details .group-block-content');
    const side = this.querySelector('.ttg-family__side');
    if (!inhalt || !side || !window.matchMedia) return;
    const teile = [...inhalt.children].filter((c) => !c.matches('variant-picker, .buy-buttons-block, .view-product-title, script'));
    if (!teile.length) return;
    const kopf = document.createElement('div');
    kopf.className = 'ttg-family__kopf';
    const marken = teile.map((c) => { const m = document.createComment('ttg-family-kopf'); c.before(m); return m; });
    const mq = window.matchMedia('(min-width: 861px)');
    const setze = () => {
      if (mq.matches) { teile.forEach((c) => kopf.appendChild(c)); side.prepend(kopf); document.body.classList.add('ttg-family-kopf-oben'); }
      else { teile.forEach((c, i) => marken[i].after(c)); kopf.remove(); document.body.classList.remove('ttg-family-kopf-oben'); }
    };
    setze();
    if (mq.addEventListener) mq.addEventListener('change', setze);
  }

  // ---------- Produktfoto-Galerie (Muster ttg-theme-adapter.setupGallery) ----------

  // Sichtbarkeit des Foto-Overlays läuft über die KLASSE is-an, nie über das
  // hidden-Attribut allein: base.css setzt img{display:block} und schlägt
  // damit die UA-Regel [hidden]{display:none} (Cascade-Origin vor Spezifität)
  // — das Overlay deckte so permanent die Bühne ab (Root Cause 21.08.2026).
  // hidden wird als a11y-Semantik weiter mitgeführt.
  _setupGallery() {
    const track = this.querySelector('[data-ttg-family-gal-track]');
    this._stagePhoto = this.querySelector('[data-ttg-family-stage-photo]');
    if (!track || !this._stagePhoto) return;
    track.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-ttg-family-gal-src]');
      if (!btn) return;
      if (btn.getAttribute('aria-pressed') === 'true') { this._showLive(); return; }
      this._galZeige(btn);
    });
    this._galBlaettern(track);
  }

  _galZeige(btn) {
    const track = this.querySelector('[data-ttg-family-gal-track]');
    track.querySelectorAll('[data-ttg-family-gal-src]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    this._stagePhoto.src = btn.dataset.ttgFamilyGalSrc;
    this._stagePhoto.alt = btn.dataset.ttgFamilyGalAlt || '';
    this._stagePhoto.classList.add('is-an');
    this._stagePhoto.hidden = false;
      // Canvas bleibt im Layout (kein CLS), das Foto liegt als Overlay darüber.
    if (this._canvas) this._canvas.style.visibility = 'hidden';
  }

  // Handy (23.10.2026, Vorgabe Jens): Pfeile auf der Bühne + Wischen, Muster ttg-baukasten.js _galerie (cddcb2e).
  // Position 0 = Live-Vorschau, 1..n = Produktfotos (wie Engine und Baukasten), blättert im Kreis. Pfeile zeigt nur das CSS
  // unter 750 px; Wischen über Touch-Events (iOS Safari bricht schräge Pointer-Gesten ab), Maus wischt nicht.
  _galSchritt(dir) {
    const track = this.querySelector('[data-ttg-family-gal-track]');
    const k = track ? [...track.querySelectorAll('[data-ttg-family-gal-src]')] : [];
    if (!k.length) return;
    const n = k.length + 1;
    const ziel = (k.findIndex((b) => b.getAttribute('aria-pressed') === 'true') + 1 + dir + n) % n;
    if (ziel === 0) this._showLive(); else this._galZeige(k[ziel - 1]);
  }

  _galBlaettern(track) {
    const stage = this.querySelector('.ttg-family__stage');
    if (!stage || !track.querySelector('[data-ttg-family-gal-src]')) return;
    for (const [dir, text, cls] of [[-1, '‹', 'zurueck'], [1, '›', 'vor']]) {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'ttg-family__gal-pfeil ttg-family__gal-pfeil--' + cls; b.textContent = text;
      b.setAttribute('aria-label', dir < 0 ? 'Vorheriges Bild' : 'Nächstes Bild');
      b.addEventListener('click', () => this._galSchritt(dir));
      stage.appendChild(b);
    }
    let start = null;
    stage.addEventListener('touchstart', (e) => {
      const t = e.touches;
      start = t.length === 1 && !e.target.closest('.ttg-family__gal-pfeil') ? { x: t[0].clientX, y: t[0].clientY, quer: false } : null;
    }, { passive: true });
    stage.addEventListener('touchmove', (e) => {
      if (!start) return;
      if (e.touches.length > 1) { start = null; return; }
      const dx = e.touches[0].clientX - start.x, dy = e.touches[0].clientY - start.y;
      if (!start.quer) {
        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) start.quer = true;
        else if (Math.abs(dy) > 10) { start = null; return; }        // senkrecht: Seite scrollt
      }
      if (start.quer && e.cancelable) e.preventDefault();
    }, { passive: false });
    stage.addEventListener('touchend', (e) => {
      const s = start, t = e.changedTouches[0];
      start = null;
      if (!s || !s.quer || !t || e.touches.length) return;
      const dx = t.clientX - s.x;
      if (Math.abs(dx) > 45) this._galSchritt(dx < 0 ? 1 : -1);
    });
    stage.addEventListener('touchcancel', () => { start = null; });
  }

  _showLive() {
    if (!this._stagePhoto || !this._stagePhoto.classList.contains('is-an')) return;
    this._stagePhoto.classList.remove('is-an');
    this._stagePhoto.hidden = true;
    this._stagePhoto.removeAttribute('src');
    if (this._canvas) this._canvas.style.visibility = '';
    const track = this.querySelector('[data-ttg-family-gal-track]');
    if (track) track.querySelectorAll('[data-ttg-family-gal-src]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
  }

  // ---------- Schwebende Mini-Vorschau (Mobile, Muster Engine-PiP) ----------
  // Erscheint, wenn die Bühne KOMPLETT aus dem Viewport ist; verschwindet ab
  // 35 % Sichtbarkeit (dieselbe Hysterese + 120-ms-Debounce wie die Engine,
  // ttg-engine.js _setupMiniPreview). Quelle ist derselbe Renderpfad: bei
  // jedem drawPreview wird der Haupt-Canvas auf die Kachel geblittet — jede
  // Konfig-Änderung ist sofort drauf. Ziehen, Einrasten, Pinch-Zoom und
  // Doppeltipp seit 23.10.2026 wie im Baukasten (_miniGesten unten); Tipp
  // auf die Kachel scrollt zurück zur Bühne.
  _setupMini() {
    if (typeof IntersectionObserver === 'undefined' || !this._canvas) return;
    const stage = this.querySelector('.ttg-family__stage');
    if (!stage) return;
    const mq = window.matchMedia('(max-width: 749px)');
    const wrap = document.createElement('div');
    wrap.className = 'ttg-family__mini';
    wrap.setAttribute('aria-hidden', 'true');
    const mc = document.createElement('canvas');
    wrap.appendChild(mc);
    // Sticky-Header-Sperrzone wie ttg-theme-adapter.setMiniInsetTop
    const header = document.getElementById('header-component');
    if (header && header.hasAttribute('sticky')) {
      const h = header.getBoundingClientRect().height;
      if (h > 0) wrap.style.setProperty('--ttg-family-mini-top', Math.round(h + 8) + 'px');
    }
    this.appendChild(wrap);
    this._mini = { wrap, canvas: mc, an: false, t: 0 };
    this._miniGesten(wrap, stage);
    const io = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      clearTimeout(this._mini.t);
      this._mini.t = setTimeout(() => {
        if (!mq.matches) { this._miniAus(); return; }
        if (!e.isIntersecting) this._miniAn();
        else if (e.intersectionRatio >= 0.35) this._miniAus();
      }, 120);
    }, { threshold: [0, 0.35] });
    io.observe(stage);
  }

  // Mini ziehen/zoomen (23.10.2026, Vorgabe Jens): Logik 1:1 aus ttg-baukasten.js _miniGesten (fm8-shop1 C), nur this._miniZ →
  // this._mini. Einfacher Tipp (nach 330 ms ohne zweiten) scrollt zur Bühne — ersetzt den früheren Klick-Handler.
  // fm8-shop1 (C): Mini-Vorschau mit einem Finger verschieben (rastet links/rechts ein), mit zwei Fingern zoomen (1–2,5×),
  // Doppeltipp setzt den Zoom zurück, einfacher Tipp springt zur Bühne. Muster: Engine _initMiniPreview (dort NICHTS geändert);
  // anders als dort bleibt der Zoom stehen, bis doppelt getippt wird (Briefing fm8-shop1 C).
  _miniGesten(wrap, stage) {
    const z = this._mini;
    Object.assign(z, { dx: 0, dy: 0, scale: 1, drag: null, pinch: null, gesture: null, letzterTipp: 0, tippT: 0, raf: 0 });
    const RAND = 12, MAX = 2.5;
    const pts = new Map();
    const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
    const dist = () => { const p = [...pts.values()]; return Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y) || 1; };
    const vw = () => (window.visualViewport ? window.visualViewport.width : innerWidth);
    const vh = () => (window.visualViewport ? window.visualViewport.height : innerHeight);
    // fm8-shop3 (F): bei offener Bildschirmtastatur verschiebt der Browser den sichtbaren Ausschnitt — Versatz einrechnen (wie Engine)
    const vv = window.visualViewport || null;
    const apply = () => { wrap.style.transform = 'translate3d(' + (z.dx + (vv ? vv.offsetLeft : 0)) + 'px,' + (z.dy + (vv ? vv.offsetTop : 0)) + 'px,0)' + (z.scale !== 1 ? ' scale(' + z.scale + ')' : ''); };
    const plan = () => { if (!z.raf) z.raf = requestAnimationFrame(() => { z.raf = 0; apply(); }); };
    const heim = () => ({ l: wrap.offsetLeft, t: wrap.offsetTop, w: wrap.offsetWidth, h: wrap.offsetHeight });
    const einrasten = () => {
      const h0 = heim();
      const links = h0.l + z.dx + h0.w * z.scale / 2 < vw() / 2;
      z.dx = links ? 0 : vw() - h0.w * z.scale - RAND - h0.l;
      z.dy = clamp(h0.t + z.dy, h0.t, vh() - h0.h * z.scale - RAND) - h0.t;
      wrap.style.transformOrigin = (links ? 'left' : 'right') + ' top';
      if (!links) z.dx = vw() - h0.w - RAND - h0.l;              // Ursprung rechts: Kachelrand bleibt am Rand
      wrap.classList.add('is-settling'); apply();
      setTimeout(() => wrap.classList.remove('is-settling'), 220);
    };
    this._miniEinrasten = einrasten;
    const tipp = () => {
      const jetzt = Date.now();
      if (jetzt - z.letzterTipp < 320) {                            // Doppeltipp: Zoom zurück
        clearTimeout(z.tippT); z.letzterTipp = 0;
        z.scale = 1; wrap.classList.add('is-settling'); apply(); setTimeout(() => wrap.classList.remove('is-settling'), 220);
        return;
      }
      z.letzterTipp = jetzt;
      z.tippT = setTimeout(() => { z.letzterTipp = 0; stage.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, 330);
    };
    wrap.addEventListener('pointerdown', (e) => {
      if (!z.an) return;
      e.preventDefault();
      if (e.isPrimary) { pts.clear(); z.drag = null; z.pinch = null; }
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try { wrap.setPointerCapture(e.pointerId); } catch (err) { /* egal */ }
      if (pts.size === 1) z.drag = { px: e.clientX, py: e.clientY, sx: z.dx, sy: z.dy, weg: false };
      else if (pts.size === 2) { z.drag = null; z.pinch = { d0: dist(), base: z.scale }; wrap.classList.add('is-zooming'); }
    });
    wrap.addEventListener('pointermove', (e) => {
      if (pts.has(e.pointerId)) pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (z.pinch) { if (pts.size >= 2) { z.scale = clamp(z.pinch.base * dist() / z.pinch.d0, 1, MAX); plan(); } return; }
      if (!z.drag) return;
      const ddx = e.clientX - z.drag.px, ddy = e.clientY - z.drag.py;
      if (Math.abs(ddx) + Math.abs(ddy) > 6) z.drag.weg = true;
      z.dx = z.drag.sx + ddx; z.dy = z.drag.sy + ddy; plan();
    });
    const hoch = (e) => {
      pts.delete(e.pointerId);
      if (z.pinch) { if (pts.size < 2) { z.pinch = null; wrap.classList.remove('is-zooming'); einrasten(); } return; }
      if (!z.drag) return;
      const weg = z.drag.weg;
      z.drag = null;
      if (weg) einrasten(); else if (e.type === 'pointerup') tipp();
    };
    wrap.addEventListener('pointerup', hoch);
    wrap.addEventListener('pointercancel', hoch);
    // iOS Safari: Zwei-Finger-Geste auf der Kachel nicht zum Seiten-Zoom werden lassen (wie Engine)
    const np = { passive: false };
    const mehr = (e) => { if (e.touches && e.touches.length > 1) e.preventDefault(); };
    wrap.addEventListener('touchstart', mehr, np);
    wrap.addEventListener('touchmove', mehr, np);
    wrap.addEventListener('gesturestart', (e) => { if (!z.an) return; e.preventDefault(); z.drag = null; if (!z.pinch) z.gesture = { base: z.scale }; }, np);
    wrap.addEventListener('gesturechange', (e) => { if (!z.an) return; e.preventDefault(); if (z.pinch) return; if (!z.gesture) z.gesture = { base: z.scale / (e.scale || 1) }; z.scale = clamp(z.gesture.base * (e.scale || 1), 1, MAX); plan(); }, np);
    wrap.addEventListener('gestureend', (e) => { if (!z.an) return; e.preventDefault(); pts.clear(); if (z.gesture) { z.gesture = null; einrasten(); } }, np);
    window.addEventListener('resize', () => { if (z.an && !z.drag && !z.pinch) einrasten(); });
    if (vv) {
      vv.addEventListener('scroll', () => { if (z.an && !z.drag && !z.pinch) apply(); });
      vv.addEventListener('resize', () => { if (z.an && !z.drag && !z.pinch) einrasten(); });
    }
  }

  _miniAn() {
    if (!this._mini || this._mini.an) return;
    this._mini.an = true;
    this._mini.wrap.classList.add('is-an');
    this._blitMini();
  }

  _miniAus() {
    if (!this._mini || !this._mini.an) return;
    this._mini.an = false;
    this._mini.wrap.classList.remove('is-an');
  }

  _blitMini() {
    const m = this._mini;
    if (!m || !m.an || !this._canvas || this._canvas.width < 10) return;
    const ziel = 480;
    const h = Math.round(ziel * this._canvas.height / this._canvas.width);
    if (m.canvas.width !== ziel || m.canvas.height !== h) { m.canvas.width = ziel; m.canvas.height = h; }
    m.canvas.getContext('2d').drawImage(this._canvas, 0, 0, ziel, h);
  }

  // ---------- Cart-Handover Variante B: natives Formular kontinuierlich füttern ----------
  _syncCart() {
    if (!this._canvas) return;
    // Layout frisch aus dem State rechnen (auflösungsunabhängig; der Kontext
    // dient nur der Textmessung) — nie vom Zeichenpfad abhängen.
    this.lastLayout = computeFamilyLayout(this.state, {
      figures: this.figures, heartAR: this.heartAR,
      measure: makeCtxMeasure(this._canvas.getContext('2d')),
    });
    // Nach einem Kaufklick mit zu breitem Namen: Meldung live nachfuehren.
    if (this._tooWideShown) this._gate(false);
    const form = this._form();
    if (!form) return;

    const rv = this.optMap
      ? resolveFamilyVariant(this.product, this.catalog, this.state.mat, this.state.size, this.optionNames)
      : null;
    if (rv) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) idInput.value = String(rv.id);
      this._note(rv.available ? '' : (this._strings().unavailable || 'Diese Kombination ist gerade nicht verfügbar.'));
    } else if (this.optMap) {
      console.warn('[ttg-family] Keine Variante für', this.state.mat, this.state.size);
    }

    // Gleiche Mechanik wie ttg-theme-adapter.syncProperties: eigene hidden
    // inputs ersetzen, Leerwerte verwerfen (kein properties[X]="" im Cart).
    const props = buildFamilyProperties(this.state, this.lastLayout, this.figures);
    form.querySelectorAll('input[data-ttg-family-prop]').forEach((n) => n.remove());
    Object.keys(props).forEach((key) => {
      const value = props[key];
      if (value == null || value === '') return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + key + ']';
      input.value = value;
      input.setAttribute('data-ttg-family-prop', '');
      form.appendChild(input);
    });
  }

  // Zentraler Änderungsweg. structural=true → Mitglieder-Liste neu rendern.
  //
  // REIHENFOLGE IST KAUFWEG-KRITISCH (Befund 21.08.2026 an den neuen Farben
  // Wiese/Kork): _touch() — und damit Variant-ID und Properties am nativen
  // Formular — läuft VOR dem Bild-Download, nie danach. Vorher hing der
  // Warenkorb-Zustand am `await this._ensureImages()`: Wer eine Farbe
  // anklickte, deren Hintergrund noch nicht im Cache lag, und sofort kaufte,
  // bekam die VORIGE Farbe in den Warenkorb (gemessen: Klick auf Kork →
  // Formular trug 200 ms lang noch die Schwarz-Variante). Dieselbe Klasse
  // wie die rAF-Entkopplung aus Session 2: der Kaufweg darf von keinem
  // asynchronen Nebenweg abhängen. Nach dem Laden wird nur noch neu
  // gezeichnet.
  async setState(patch, opts = {}) {
    Object.assign(this.state, patch);
    if (opts.structural) this._renderMembers();
    if (opts.controls) this._renderControls();
    this._touch();
    await this._ensureImages();
    this._scheduleDraw();
    if (document.body.classList.contains('ttg-family-active')) this._ladeDruckBild().catch(() => {});
  }

  // ---------- Mitglieder-UI + Steuerelemente ----------

  _buildUI() {
    const host = this.querySelector('[data-ttg-family-ui]');
    if (!host) return;
    host.textContent = '';

    // Familienname
    const nameField = el('div', 'ttg-family__field');
    const nameLabel = el('label', 'ttg-family__label', 'Familienname');
    nameLabel.setAttribute('for', 'ttg-family-famname');
    const nameInput = el('input', 'ttg-family__input');
    nameInput.type = 'text';
    nameInput.id = 'ttg-family-famname';
    nameInput.placeholder = 'z. B. Müller';
    nameInput.maxLength = FAMILY_LIMITS.famNameMax;
    nameInput.value = this.state.famName;
    nameInput.addEventListener('input', () => {
      // maxlength deckt die Tastatur; slice fängt Paste/Autofill jenseits von 20.
      this.state.famName = nameInput.value.slice(0, FAMILY_LIMITS.famNameMax);
      if (nameInput.value.trim()) this._setGateError('');
      this._touch();
    });
    const gateError = el('p', 'ttg-family__error');
    gateError.setAttribute('data-ttg-family-error', '');
    gateError.hidden = true;
    nameField.append(nameLabel, nameInput, gateError);

    // Mitglieder
    const memberField = el('div', 'ttg-family__field');
    memberField.appendChild(el('span', 'ttg-family__label', 'Familienmitglieder'));
    this._memberList = el('div', 'ttg-family__members');
    this._memberList.setAttribute('data-ttg-family-members', '');
    const addBtn = el('button', 'ttg-family__add', '+ Mitglied hinzufügen');
    addBtn.type = 'button';
    addBtn.setAttribute('data-ttg-family-add', '');
    addBtn.addEventListener('click', () => {
      if (this.state.members.length >= FAMILY_LIMITS.maxMembers) return;
      this.state.members.push({ type: 'junge', name: '' });
      this.setState({}, { structural: true });
    });
    this._memberHint = el('p', 'ttg-family__hint');
    memberField.append(this._memberList, addBtn, this._memberHint);

    // Steuerelemente (Abstand, Herz, Ausrichtung, Mattenfarbe, Größe)
    this._controls = el('div', 'ttg-family__controls');
    host.append(nameField, memberField, this._controls);

    this._renderMembers();
    this._renderControls();
  }

  _renderMembers() {
    const list = this._memberList;
    if (!list) return;
    list.textContent = '';
    const tooWideEls = [];
    this._tooWideEls = tooWideEls;
    this.state.members.forEach((m, i) => {
      const row = el('div', 'ttg-family__member');

      // Die Figur IST das Auswahlelement (21.08.2026). Vorher teilten sich ein
      // separates Auswahlfeld und das Namensfeld den Rest der Zeile
      // (minmax(0,1fr) je) — auf einem 375er-Display blieben beiden rund 92px,
      // und aus "Vater" wurde "Vate". Das Auswahlfeld ist entfallen, seinen
      // Platz erbt der Name; die Figur oeffnet das Auswahlblatt.
      const fig = this.figures[m.type];
      const figbtn = el('button', 'ttg-family__figbtn');
      figbtn.type = 'button';
      figbtn.dataset.ttgFigbtn = String(i);
      figbtn.setAttribute('aria-haspopup', 'dialog');
      figbtn.setAttribute('aria-label', 'Figur wählen — aktuell ' + fig.label);
      const thumb = el('span', 'ttg-family__thumb');
      const timg = document.createElement('img');
      timg.src = cdnSize(fig.url, 80);
      timg.alt = '';
      timg.loading = 'lazy';
      // 40px Basis statt 34: die Kachel ist seit dem Umbau das BEDIENelement,
      // nicht mehr nur eine Anzeige — und die relative Hoehe ist bei
      // Strichmaennchen das Hauptunterscheidungsmerkmal (Junge/Teen/Vater
      // unterscheiden sich fast nur darin). Sie bleibt deshalb relativ.
      timg.style.height = Math.round(40 * fig.rel) + 'px';
      thumb.appendChild(timg);
      // Bezeichnung unter der Figur: der Typ bleibt ohne Tippen ablesbar.
      // Klammern raus, damit "Teen (Junge)" sauber an der Wortgrenze umbricht.
      thumb.appendChild(el('span', 'ttg-family__thumb-cap', fig.label.replace(/[()]/g, '')));
      figbtn.appendChild(thumb);
      figbtn.addEventListener('click', () => this._openFigureSheet(i, figbtn));

      const inp = document.createElement('input');
      inp.type = 'text';
      inp.className = 'ttg-family__input ttg-family__input--name';
      inp.placeholder = 'Name (optional)';
      inp.maxLength = FAMILY_LIMITS.nameMax;
      inp.value = m.name;
      inp.setAttribute('aria-label', 'Name für ' + fig.label);
      // Hinweis unter genau dieser Zeile, sobald die Grenze erreicht ist.
      // maxLength kuerzt Einfuegen bereits im Browser; familyNameInput kuerzt
      // zusaetzlich (Programmwerte, alte Browser) und entscheidet den Hinweis.
      const limitHint = el('p', 'ttg-family__hint ttg-family__hint--limit', FAMILY_NAME_LIMIT_HINT);
      limitHint.setAttribute('role', 'status');
      const syncName = () => {
        const r = familyNameInput(inp.value);
        if (inp.value !== r.value) inp.value = r.value;
        m.name = r.value;
        limitHint.hidden = !r.atLimit;
      };
      syncName();
      inp.addEventListener('input', () => { syncName(); this._touch(); });

      const ops = el('span', 'ttg-family__ops');
      const mkOp = (txt, title, fn, disabled, extra) => {
        const b = el('button', 'ttg-family__op' + (extra ? ' ' + extra : ''), txt);
        b.type = 'button';
        b.title = title;
        b.disabled = !!disabled;
        b.addEventListener('click', fn);
        ops.appendChild(b);
      };
      mkOp('↑', 'Nach vorne', () => {
        [this.state.members[i - 1], this.state.members[i]] = [this.state.members[i], this.state.members[i - 1]];
        this.setState({}, { structural: true });
      }, i === 0);
      mkOp('↓', 'Nach hinten', () => {
        [this.state.members[i + 1], this.state.members[i]] = [this.state.members[i], this.state.members[i + 1]];
        this.setState({}, { structural: true });
      }, i === this.state.members.length - 1);
      // Nur das Kreuz traegt die Fehlerfarbe (--ttg-err); die Pfeile bleiben
      // neutral — Loeschen ist die einzige nicht umkehrbare Aktion der Zeile.
      mkOp('✕', 'Entfernen', () => {
        this.state.members.splice(i, 1);
        this.setState({}, { structural: true });
      }, false, 'ttg-family__op--del');

      const wideMsg = el('p', 'ttg-family__error', FAMILY_NAME_TOO_WIDE);
      wideMsg.setAttribute('role', 'alert');
      wideMsg.hidden = true;
      tooWideEls.push({ input: inp, msg: wideMsg });

      row.append(figbtn, inp, ops);
      list.appendChild(row);
      list.appendChild(limitHint);
      list.appendChild(wideMsg);
    });

    const addBtn = this.querySelector('[data-ttg-family-add]');
    if (addBtn) addBtn.disabled = this.state.members.length >= FAMILY_LIMITS.maxMembers;
    if (this._memberHint) {
      this._memberHint.textContent = this.state.members.length + ' von ' + FAMILY_LIMITS.maxMembers
        + ' Plätzen belegt · max. ' + FAMILY_LIMITS.nameMax + ' Zeichen pro Name · Reihenfolge links → rechts.';
    }
  }

  _seg(labelText, entries, current, onPick) {
    const field = el('div', 'ttg-family__field');
    field.appendChild(el('span', 'ttg-family__label', labelText));
    const seg = el('div', 'ttg-family__seg');
    for (const [value, label] of entries) {
      const b = el('button', 'ttg-family__seg-btn' + (value === current ? ' on' : ''), label);
      b.type = 'button';
      b.addEventListener('click', () => onPick(value));
      seg.appendChild(b);
    }
    field.appendChild(seg);
    return field;
  }

  _renderControls() {
    const host = this._controls;
    if (!host) return;
    host.textContent = '';

    // Abstand-Stepper: Grenzen und Schrittweite kommen ausschliesslich aus
    // FAMILY_LIMITS (10–50 mm, Schritt 5, Standard 30 — Vorgabe Jens
    // 21.08.2026). Hier stehen bewusst KEINE Zahlen mehr: der frühere
    // Kommentar "6–30 mm, Schritt 4" war nach der Umstellung falsch und
    // hätte die nächste Änderung in die Irre geführt.
    const gapField = el('div', 'ttg-family__field');
    gapField.appendChild(el('span', 'ttg-family__label', 'Abstand zwischen den Figuren'));
    const stepper = el('div', 'ttg-family__stepper');
    const minus = el('button', 'ttg-family__op', '−');
    minus.type = 'button';
    minus.title = 'Abstand verkleinern';
    minus.disabled = this.state.gapMm <= FAMILY_LIMITS.gapMin;
    minus.addEventListener('click', () => {
      this.setState({ gapMm: Math.max(FAMILY_LIMITS.gapMin, this.state.gapMm - FAMILY_LIMITS.gapStep) }, { controls: true });
    });
    const gapVal = el('span', 'ttg-family__gap-val', this.state.gapMm + ' mm');
    const plus = el('button', 'ttg-family__op', '+');
    plus.type = 'button';
    plus.title = 'Abstand vergrößern';
    plus.disabled = this.state.gapMm >= FAMILY_LIMITS.gapMax;
    plus.addEventListener('click', () => {
      this.setState({ gapMm: Math.min(FAMILY_LIMITS.gapMax, this.state.gapMm + FAMILY_LIMITS.gapStep) }, { controls: true });
    });
    stepper.append(minus, gapVal, plus);
    gapField.appendChild(stepper);
    host.appendChild(gapField);

    // Herz
    host.appendChild(this._seg('Herz', Object.entries(HEART_LABELS), this.state.heart,
      (v) => this.setState({ heart: v }, { controls: true })));

    // Ausrichtung
    host.appendChild(this._seg('Ausrichtung', Object.entries(ALIGN_LABELS), this.state.align,
      (v) => this.setState({ align: v }, { controls: true })));

    // Mattenfarbe: aus den Produkt-Optionswerten (∩ Katalog); ohne Produkt
    // (Sichtprobe) direkt aus dem Katalog.
    const matKeys = this.optMap ? Object.keys(this.optMap.matValueByKey) : Object.keys(this.catalog.hintergruende || {});
    if (matKeys.length) {
      const matField = el('div', 'ttg-family__field');
      matField.appendChild(el('span', 'ttg-family__label', 'Mattenfarbe'));
      const row = el('div', 'ttg-family__swatches');
      for (const key of matKeys) {
        const bg = this.catalog.hintergruende[key];
        const b = el('button', 'ttg-family__swatch' + (key === this.state.mat ? ' on' : ''));
        b.type = 'button';
        b.title = this.optMap ? this.optMap.matValueByKey[key] : bg.label;
        b.style.backgroundColor = bg.fallback;
        b.style.backgroundImage = 'url(' + cdnSize(bg.url, 80) + ')';
        b.addEventListener('click', () => this.setState({ mat: key }, { controls: true }));
        row.appendChild(b);
      }
      matField.appendChild(row);
      host.appendChild(matField);
    }

    // Größe: Karten wie im Engine-Konfigurator (_sizeRow) — Options-Wert
    // wörtlich + Preis und Streichpreis der Variante zur AKTUELLEN Mattenfarbe.
    const sizeEntries = this.optMap
      ? Object.entries(this.optMap.sizeValueByKey)
      : Object.keys(FAMILY_PRINT_SIZES).map((k) => [k, k.replace('x', ' × ') + ' cm']);
    const sizeField = el('div', 'ttg-family__field');
    sizeField.appendChild(el('span', 'ttg-family__label', 'Größe'));
    const cards = el('div', 'ttg-family__sizes');
    for (const [key, label] of sizeEntries) {
      const b = el('button', 'ttg-family__size-card');
      b.type = 'button';
      b.setAttribute('aria-pressed', String(key === this.state.size));
      b.appendChild(el('span', 'ttg-family__size-v', label));
      const rv = this.optMap ? resolveFamilyVariant(this.product, this.catalog, this.state.mat, key, this.optionNames) : null;
      if (rv && rv.price != null) {
        const priceWrap = el('span', 'ttg-family__size-price');
        priceWrap.appendChild(el('span', null, formatMoneyEUR(rv.price)));
        if (rv.compareAtPrice && rv.compareAtPrice > rv.price) {
          priceWrap.appendChild(el('s', null, formatMoneyEUR(rv.compareAtPrice)));
        }
        b.appendChild(priceWrap);
      }
      b.addEventListener('click', () => this.setState({ size: key }, { controls: true }));
      cards.appendChild(b);
    }
    sizeField.appendChild(cards);
    if (this.sizeTable) {
      const guide = el('button', 'ttg-family__sizeguide-link', 'Größenratgeber');
      guide.type = 'button';
      guide.addEventListener('click', () => this._openSizeGuide());
      sizeField.appendChild(guide);
    }
    host.appendChild(sizeField);
  }

  // ---------- Größenratgeber-Overlay (Muster Hauptsection: Panel von rechts,
  // Lazy-Aufbau beim ersten Klick, note vor der Tabelle, ESC/Backdrop/×) ----------

  _openSizeGuide() {
    if (!this.sizeTable) return;
    if (!this._sizeOverlay) this._sizeOverlay = this._buildSizeGuide();
    this._sizeOverlay.backdrop.hidden = false;
    this._sizeOverlay.panel.hidden = false;
    this._sizeOverlay.close.focus();
    this._ladeVisualizer();
  }

  // Türbreiten-Visualizer: lazy über die dokumentierte Engine-Naht
  // TTG_SIZE_OVERLAY_RENDERERS (ttg-engine-api.md — „extern = Erweiterungs-
  // naht"). Der doormat-visualizer ignoriert nachweislich beide Parameter
  // und init braucht nur das Container-Element — kein Engine-Mount nötig.
  // Import erst beim ersten Öffnen des Ratgebers: null Kosten beim Seitenladen.
  async _ladeVisualizer() {
    const engineSrc = this._config().engineSrc;
    if (this._dvGeladen || !engineSrc || !this._sizeOverlay) return;
    this._dvGeladen = true;
    try {
      const mod = await import(engineSrc);
      const renderer = mod.TTG_SIZE_OVERLAY_RENDERERS && mod.TTG_SIZE_OVERLAY_RENDERERS['doormat-visualizer'];
      if (!renderer) return;
      const r = renderer(null, {});
      const wrap = el('div', 'ttg-family__ovl-dv');
      wrap.innerHTML = typeof r === 'string' ? r : r.html;   // Engine-generiertes Markup, keine Nutzerdaten
      this._sizeOverlay.panel.appendChild(wrap);
      if (r && typeof r.init === 'function') r.init(wrap);
    } catch (e) {
      this._dvGeladen = false;
      console.warn('[ttg-family] Türbreiten-Visualizer nicht ladbar:', e);
    }
  }

  _closeSizeGuide() {
    if (!this._sizeOverlay) return;
    this._sizeOverlay.backdrop.hidden = true;
    this._sizeOverlay.panel.hidden = true;
  }

  // ---------- Auswahlblatt für die Figur ----------
  // Ein einziges Blatt für alle Zeilen; welche Zeile es bedient, steht in
  // _figSheetIndex. Aufbau nach demselben Muster wie der Größenratgeber
  // (Backdrop + aside[role=dialog] + Kopf mit ×), damit sich beide gleich
  // anfühlen und dieselben CSS-Klassen tragen.
  _openFigureSheet(index, ausloeser) {
    if (!this._figSheet) this._figSheet = this._buildFigureSheet();
    const m = this.state.members[index];
    if (!m) return;
    this._figSheetIndex = index;
    this._figSheetOpener = ausloeser || null;
    for (const t of this._figSheet.tiles) {
      const an = t.dataset.key === m.type;
      t.classList.toggle('on', an);
      t.setAttribute('aria-checked', an ? 'true' : 'false');
      t.tabIndex = an ? 0 : -1;
    }
    this._figSheet.backdrop.hidden = false;
    this._figSheet.panel.hidden = false;
    const aktiv = this._figSheet.tiles.find((t) => t.dataset.key === m.type);
    (aktiv || this._figSheet.close).focus();
  }

  _closeFigureSheet(fokusZurueck = true) {
    if (!this._figSheet || this._figSheet.panel.hidden) return;
    this._figSheet.backdrop.hidden = true;
    this._figSheet.panel.hidden = true;
    const opener = this._figSheetOpener;
    this._figSheetOpener = null;
    if (fokusZurueck && opener && document.contains(opener)) opener.focus();
  }

  _pickFigure(key) {
    const i = this._figSheetIndex;
    const m = this.state.members[i];
    // Ohne Fokus-Rueckgabe schliessen: _renderMembers ersetzt den ausloesenden
    // Knopf gleich, der Fokus geht danach an den NEUEN Knopf derselben Zeile.
    this._closeFigureSheet(false);
    if (!m) return;
    if (m.type === key) { this._fokusFigbtn(i); return; }
    m.type = key;
    this.setState({}, { structural: true });
    this._fokusFigbtn(i);
  }

  _fokusFigbtn(i) {
    const b = this.querySelector(`[data-ttg-figbtn="${i}"]`);
    if (b) b.focus();
  }

  _buildFigureSheet() {
    const backdrop = el('div', 'ttg-family__ovl-backdrop');
    backdrop.addEventListener('click', () => this._closeFigureSheet());
    const panel = el('aside', 'ttg-family__ovl ttg-family__ovl--figs');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', 'Figur wählen');
    panel.dataset.ttgFigureSheet = '1';
    const head = el('div', 'ttg-family__ovl-head');
    head.appendChild(el('h3', 'ttg-family__ovl-title', 'Figur wählen'));
    const close = el('button', 'ttg-family__ovl-close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Schließen');
    close.addEventListener('click', () => this._closeFigureSheet());
    head.appendChild(close);
    panel.appendChild(head);

    const grid = el('div', 'ttg-family__figgrid');
    grid.setAttribute('role', 'radiogroup');
    const tiles = [];
    for (const f of this.catalog.figuren) {
      const t = el('button', 'ttg-family__figtile');
      t.type = 'button';
      t.dataset.key = f.key;
      t.setAttribute('role', 'radio');
      const box = el('span', 'ttg-family__figtile-img');
      const img = document.createElement('img');
      img.src = cdnSize(f.url, 160);
      img.alt = '';
      img.loading = 'lazy';
      // Gleiche Groessenlogik wie auf der Buehne: Hoehe relativ zum Vater.
      img.style.height = Math.round(58 * this.figures[f.key].rel) + 'px';
      box.appendChild(img);
      t.append(box, el('span', 'ttg-family__figtile-cap', f.label));
      t.addEventListener('click', () => this._pickFigure(f.key));
      tiles.push(t);
      grid.appendChild(t);
    }
    panel.appendChild(grid);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !panel.hidden) this._closeFigureSheet();
    });
    backdrop.hidden = true;
    panel.hidden = true;
    this.append(backdrop, panel);
    return { backdrop, panel, close, tiles };
  }

  // Aufbau komplett über DOM-API/textContent — Metafeld-Text braucht so
  // kein Escaping und kann nie als Markup laufen.
  _buildSizeGuide() {
    const st2 = this.sizeTable;
    const backdrop = el('div', 'ttg-family__ovl-backdrop');
    backdrop.addEventListener('click', () => this._closeSizeGuide());
    const panel = el('aside', 'ttg-family__ovl');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Größenratgeber');
    const head = el('div', 'ttg-family__ovl-head');
    head.appendChild(el('h3', 'ttg-family__ovl-title', 'Größenratgeber'));
    const close = el('button', 'ttg-family__ovl-close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Schließen');
    close.addEventListener('click', () => this._closeSizeGuide());
    head.appendChild(close);
    panel.appendChild(head);
    if (st2.note) panel.appendChild(el('p', 'ttg-family__ovl-note', st2.note));
    const wrap = el('div', 'ttg-family__ovl-tablewrap');
    const table = el('table', 'ttg-family__ovl-table');
    const thead = document.createElement('thead');
    const hr = document.createElement('tr');
    st2.columns.forEach((c) => hr.appendChild(el('th', null, c)));
    thead.appendChild(hr);
    const tbody = document.createElement('tbody');
    st2.rows.forEach((r) => {
      const tr = document.createElement('tr');
      r.forEach((c) => tr.appendChild(el('td', null, c)));
      tbody.appendChild(tr);
    });
    table.append(thead, tbody);
    wrap.appendChild(table);
    panel.appendChild(wrap);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this._closeSizeGuide(); });
    backdrop.hidden = true;
    panel.hidden = true;
    this.append(backdrop, panel);
    return { backdrop, panel, close };
  }
  }
  customElements.define('ttg-family-customizer', TTGFamilyCustomizer);
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined' && !customElements.get('ttg-family-customizer')) {
  defineFamilyElement();
}
